'use client';

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/total-operations/workflows/service-operations/billing-submissions.mdx
 * (published at user-docs.totalaccess.co.za/total-operations/workflows/service-operations/billing-submissions)
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  ArrowRight,
  Briefcase,
  Clock3,
  DollarSign,
  FileText,
  FolderKanban,
  Loader2,
  Lock,
  Navigation,
  Package,
  Plus,
  Receipt,
  RefreshCw,
  Send,
  Settings,
  Users,
  Wrench,
} from 'lucide-react';
import JobCostingDashboard from '@/app/client-zone/modules/total-operations/components/manufacturing/JobCostingDashboard';
import { useAppointmentLiveTimers } from '@/hooks/useAppointmentLiveTimers';
import { useJobConfig } from '@/hooks/useJobConfig';
import { useTheme } from '@/contexts/ThemeContext';
import { notify } from '@/lib/notifications';
import {
  canShowJobBillableLedgerForStatus,
  isReadOnlyJobBillingStatus,
} from '@/lib/operations/jobBillingStatusAccess';
import { formatDateSAST, formatDateTimeSAST } from '@/lib/sastTime';
import { buildTimeBillingLines } from '@/lib/billing/timeBillingLines';
import BillingCommentsSection from './BillingCommentsSection';
import TimeTrackingTab from './TimeTrackingTab';
import JobExpensesTab from './JobExpensesTab';
import AssetMaintenanceTab from './AssetMaintenanceTab';
import ProjectTimeTrackingTab from './ProjectTimeTrackingTab';
import UserDocsLink from '@/components/shared/UserDocsLink';
import { BILLING_SUBMISSION_STATUS_DESCRIPTIONS } from '../work-items/billingSubmissionTypes';
import type { WorkItem } from '../UniversalWorkItems';
import {
  type BillingDashboardScopeType,
  type BillingSectionId,
  type JobBillingPrimarySectionId,
  type TimeSummary,
  type ProjectTimeSummary,
  type BomItem,
  type JobExpense,
  type ProjectExpenseSummary,
  type BillingRatesSummary,
  type BillingRules,
  type TeamScopeSnapshot,
  type MaintenanceRecord,
  type EffectiveJobStatusDetails,
  type JobFinancialInsights,
  type BillingWorkspaceItem,
  type BillingDashboardProps,
  budgetHealthMeta,
} from './billingDashboardTypes';

export type { BillingDashboardProps, BillingSectionId };

import {
  normalizeIds,
  getScopeType,
  formatCurrency,
  formatHours,
  loadTimerAggregationMode,
  saveTimerAggregationMode,
  normalizeTimerAggregationMode,
  getTimerAggregationModeFromCustomFields,
  persistTimerAggregationModeForWorkItem,
  loadChargeTogglesFromStorage,
  getFinanceWorkspaceAccess,
  getAuthHeaders,
  fetchJson,
  pMap,
  dedupeRecords,
  sanitizeJobFinancialInsights,
  isCompletedWorkItemStatus,
  isCancelledWorkItemStatus,
  deriveEffectiveJobStatus,
  summarizeTeamScopeFromFinancialSummary,
  summarizeTeamScopeFromTimeEntries,
  openLinkedWorkItem,
  getDocumentStatusPillClass,
  getBillingSubmissionStatusLabel,
  sectionNeedsOverview,
  type TimerAggregationMode,
  type WorkItemApiResponse,
} from './billingDashboardUtils';

import { DashboardPanel, DashboardStatCard } from './DashboardPanel';
import { useBillingSubmissions } from './useBillingSubmissions';
import { useChargeToggles } from './useChargeToggles';
import { useBillingCalculations } from './useBillingCalculations';

// --- Team time aggregation helpers (mirrors TimeTrackingTab logic) ---
const ONSITE_ACTIVITY_TYPES = ['on_site', 'job', 'work', 'checkin', 'field_work', 'maintenance', 'delivery'];

function rawEntryDurationHours(entry: Record<string, unknown>, nowMs: number): number {
  const dh = Number(entry.duration_hours);
  if (Number.isFinite(dh) && dh >= 0) return dh;
  const dm = Number(entry.duration_minutes);
  if (Number.isFinite(dm) && dm >= 0) return dm / 60;
  const startedAt = entry.started_at ? new Date(entry.started_at as string).getTime() : NaN;
  const endedAt = entry.ended_at ? new Date(entry.ended_at as string).getTime() : (entry.is_running ? nowMs : NaN);
  if (Number.isFinite(startedAt) && Number.isFinite(endedAt) && endedAt > startedAt) {
    return (endedAt - startedAt) / 3_600_000;
  }
  return 0;
}

function mergeOverlappingIntervalsHours(entries: Array<Record<string, unknown>>, nowMs: number): number {
  if (entries.length === 0) return 0;
  if (entries.length === 1) return rawEntryDurationHours(entries[0], nowMs);

  const withTs = entries.filter((e) => e.started_at);
  const withoutTs = entries.filter((e) => !e.started_at);

  let totalWithoutTs = 0;
  if (withoutTs.length > 0) {
    totalWithoutTs = Math.max(...withoutTs.map((e) => rawEntryDurationHours(e, nowMs)));
  }
  if (withTs.length === 0) return totalWithoutTs;

  const intervals = withTs.map((e) => {
    const startMs = new Date(e.started_at as string).getTime();
    let endMs: number;
    if (e.ended_at) {
      endMs = new Date(e.ended_at as string).getTime();
    } else if (e.is_running) {
      endMs = nowMs;
    } else {
      endMs = startMs + rawEntryDurationHours(e, nowMs) * 3_600_000;
    }
    return { startMs, endMs: Math.max(endMs, startMs) };
  });

  intervals.sort((a, b) => a.startMs - b.startMs);

  const merged: { startMs: number; endMs: number }[] = [];
  for (const interval of intervals) {
    const last = merged[merged.length - 1];
    if (last && interval.startMs <= last.endMs) {
      last.endMs = Math.max(last.endMs, interval.endMs);
    } else {
      merged.push({ startMs: interval.startMs, endMs: interval.endMs });
    }
  }

  const mergedHours = merged.reduce((sum, m) => sum + (m.endMs - m.startMs) / 3_600_000, 0);
  return mergedHours + totalWithoutTs;
}

function computeTeamAggregatedHours(rawEntries: Array<Record<string, unknown>>): { onsiteHours: number; travelHours: number } {
  const nowMs = Date.now();
  const onsiteBuckets = new Map<string, Array<Record<string, unknown>>>();
  const travelBuckets = new Map<string, Array<Record<string, unknown>>>();

  for (const entry of rawEntries) {
    const rawDate = (entry.started_at as string) || (entry.entry_date as string) || (entry.created_at as string);
    const dateKey = rawDate ? new Date(rawDate).toISOString().slice(0, 10) : 'no-date';
    const sourceId = String(entry.source_item_id || entry.source_item_number || entry.work_item_id || 'direct');
    const actType = String(entry.activity_type || 'other');
    const key = `${sourceId}:${dateKey}`;

    if (actType === 'travel') {
      if (!travelBuckets.has(key)) travelBuckets.set(key, []);
      travelBuckets.get(key)!.push(entry);
    } else if (ONSITE_ACTIVITY_TYPES.includes(actType)) {
      if (!onsiteBuckets.has(key)) onsiteBuckets.set(key, []);
      onsiteBuckets.get(key)!.push(entry);
    }
  }

  const onsiteHours = Array.from(onsiteBuckets.values()).reduce(
    (sum, entries) => sum + mergeOverlappingIntervalsHours(entries, nowMs), 0,
  );
  const travelHours = Array.from(travelBuckets.values()).reduce(
    (sum, entries) => sum + mergeOverlappingIntervalsHours(entries, nowMs), 0,
  );

  return {
    onsiteHours: Math.round(onsiteHours * 100) / 100,
    travelHours: Math.round(travelHours * 100) / 100,
  };
}

export default function BillingDashboard({
  permissions,
  templateKey,
  itemTypes,
  workItemId,
  workItemType,
  rootItemIds,
  includeDescendants,
  title,
  subtitle,
  embedded = false,
  showHeaderControls = !embedded,
  onOpenItem,
  onStatsChange,
  isActive = true,
  includeRelated,
  readOnly = false,
  workItem,
  defaultSection,
  onExpensesChange,
  jobBillingWorkspaceAccess = true,
  operationsJobGenerateInvoiceAccess = true,
  timersSignoffAccess = true,
  billingFinalizeAccess = true,
  processGateStatus,
  onUpdateWorkItemStatus,
  onOpenDocumentModal,
  onCreateExpense,
  ledgerGenerated: ledgerGeneratedProp,
  onLedgerGenerated,
  billingStrictness = 'standard',
  canSubmitForBilling = true,
  canReviewBilling = true,
  canApproveBilling = false,
  pendingDocType = null,
  onTimerAggregationModeChange,
}: BillingDashboardProps) {
  const { currentTheme, clientBranding } = useTheme();
  const isDark = currentTheme.mode === 'dark';
  const accentColor = clientBranding?.primary_color || currentTheme.colors.primary;
  const { getTimer: getAppointmentLiveTimer } = useAppointmentLiveTimers({ appointmentIds: workItemId ? [workItemId] : [] });
  const hasLoadedRef = useRef(false);
  const loadOverviewRef = useRef<(() => Promise<void>) | null>(null);
  const appliedDefaultSectionRef = useRef<BillingSectionId | undefined>(defaultSection);
  const initialSection = defaultSection || (workItemType === 'job' ? 'time' : workItemId ? 'submissions' : 'costing');
  const financeWorkspaceAccess = useMemo(() => getFinanceWorkspaceAccess(), []);

  const [activeSection, setActiveSection] = useState<BillingSectionId>(initialSection);
  const [ledgerGeneratedInternal, setLedgerGeneratedInternal] = useState(false);
  const [generatingBilling, setGeneratingBilling] = useState(false);
  const [requestedDocType, setRequestedDocType] = useState<'quote' | 'proforma' | 'invoice'>('invoice');
  const pendingDocTypeRef = useRef<'quote' | 'proforma' | 'invoice' | null>(null);
  const { billingSubmissions, billingSubmissionsLoading, loadBillingSubmissions } = useBillingSubmissions(workItemId);
  const ledgerGenerated = ledgerGeneratedProp ?? ledgerGeneratedInternal;
  const handleSetLedgerGenerated = useCallback((value: boolean) => {
    setLedgerGeneratedInternal(value);
    if (value) {
      onLedgerGenerated?.();
    }
  }, [onLedgerGenerated]);

  const { billingSettings, setBillingSettings, handleChargeToggle } = useChargeToggles();
  const [timerAggregationMode, setTimerAggregationMode] = useState<'individual' | 'team'>(loadTimerAggregationMode);

  // State used by handleGenerateBilling — must be declared before it
  const [timeSummary, setTimeSummary] = useState<TimeSummary | null>(null);
  const [projectTimeSummary, setProjectTimeSummary] = useState<ProjectTimeSummary | null>(null);
  const [bomItems, setBomItems] = useState<BomItem[]>([]);
  const [jobExpenses, setJobExpenses] = useState<JobExpense[]>([]);
  const [billingRates, setBillingRates] = useState<BillingRatesSummary>({
    labour_rate: 0,
    travel_time_rate: 0,
    travel_km_rate: 0,
    seeded: false,
  });
  const [billingRules, setBillingRules] = useState<BillingRules>({
    min_travel_time_hours: 0,
    min_travel_distance_km: 0,
    call_out_fee_per_appointment: 0,
    charge_call_out_per_appointment: true,
    user_labour_rates: {},
    is_synced: false,
  });
  const [defaultTaxRate, setDefaultTaxRate] = useState<number>(0);
  const [teamScopeSnapshot, setTeamScopeSnapshot] = useState<TeamScopeSnapshot | null>(null);

  const handleGenerateBilling = useCallback(async () => {
    // submit_for_billing (Actions tab matrix) gates generating/regenerating a
    // billing submission — the server enforces the same key on the
    // draft → pending_approval transition.
    if (!workItemId || generatingBilling || !canSubmitForBilling) return;
    setGeneratingBilling(true);
    try {
      // Build line items from a fresh fetch of live data (timers, BOM, expenses).
      const generatedLines: Array<Record<string, unknown>> = [];

      // --- Fetch fresh time summary (include_related=true for child appointments) ---
      // Raw time entries are fetched unconditionally — team mode merges
      // overlapping intervals from them, and both modes use the per-item
      // breakdown to emit TE-* coded lines (capacity/job-costing key on
      // TE-LABOUR:<item> / TE-LABOUR:<item>:<user> codes for attribution).
      const authHeaders = getAuthHeaders();
      const teamEntriesFetch = fetch(`/api/v1/work-items/${workItemId}/time-entries?include_related=true`, { headers: authHeaders })
        .then((r) => r.ok ? r.json() : null)
        .catch(() => null);

      const [freshSummaryRes, freshBomRes, freshExpensesRes, freshEntriesJson] = await Promise.all([
        fetch(`/api/v1/work-items/${workItemId}/time-entries/summary?include_related=true`, { headers: authHeaders }),
        fetch(`/api/v1/work-items/${workItemId}/bom?include_related=true`, { headers: authHeaders }),
        fetch('/api/v1/work-items/fleet', {
          method: 'POST',
          headers: { ...authHeaders, 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'getJobExpenses', jobId: workItemId, include_related: true }),
        }),
        teamEntriesFetch,
      ]);

      const freshSummaryData = freshSummaryRes.ok ? await freshSummaryRes.json() : null;
      const ts = freshSummaryData?.summary || freshSummaryData?.data?.summary || projectTimeSummary || timeSummary;
      let onsiteHours = ts?.onsite_hours ?? 0;
      let travelHours = ts?.travel_hours ?? 0;
      const travelKm = ts?.travel_distance_km ?? 0;

      // Raw entries feed both the team-merge override and the TE-* coded
      // per-item lines below (capacity + job-costing attribute finalized
      // billing through the item codes).
      const rawEntries = Array.isArray(freshEntriesJson?.data) ? freshEntriesJson.data : [];

      // When team aggregation mode is selected, recompute onsite/travel hours from
      // raw time entries by merging overlapping intervals per appointment+date.
      if (timerAggregationMode === 'team' && rawEntries.length > 0) {
        const teamValues = computeTeamAggregatedHours(rawEntries);
        onsiteHours = teamValues.onsiteHours;
        travelHours = teamValues.travelHours;
      }

      // Use fresh BOM and expenses if available, fall back to in-memory state
      const freshBomData = freshBomRes.ok ? await freshBomRes.json() : null;
      const freshBomItems = Array.isArray(freshBomData?.data) ? freshBomData.data : Array.isArray(freshBomData?.data?.items) ? freshBomData.data.items : bomItems;

      const freshExpensesData = freshExpensesRes.ok ? await freshExpensesRes.json() : null;
      const freshJobExpenses = Array.isArray(freshExpensesData?.expenses) ? freshExpensesData.expenses : Array.isArray(freshExpensesData?.data) ? freshExpensesData.data : jobExpenses;

      // --- Collect lines by group for structural header/subtotal insertion ---
      const groupOrder = ['time', 'fees', 'bom', 'expenses'] as const;
      const groupLabels: Record<string, string> = { time: 'Time', fees: 'Fees', bom: 'Materials', expenses: 'Expenses' };
      const linesByGroup: Record<string, Array<Record<string, unknown>>> = {};

      // TE-* coded lines carry the finalized billing that the capacity and
      // job-costing views attribute per item/member — per-(item[,user])
      // lines are emitted whenever raw entries resolved. The aggregate
      // blocks below remain as fallback for when the entry fetch fails.
      const teLines = rawEntries.length > 0
        ? buildTimeBillingLines({
            entries: rawEntries,
            rootItemNumber: workItem?.item_number ?? null,
            mode: timerAggregationMode,
            labourRate: billingRates.labour_rate || 0,
            travelTimeRate: billingRates.travel_time_rate || 0,
            travelKmRate: billingRates.travel_km_rate || 0,
            taxRate: defaultTaxRate,
            billOnsiteLabour: billingSettings.bill_onsite_labour,
            billTravelTime: billingSettings.bill_travel_time,
            billTravelKm: billingSettings.bill_travel_km,
            minTravelTimeHours: billingRules.min_travel_time_hours,
            minTravelDistanceKm: billingRules.min_travel_distance_km,
            callOutFee: billingRules.is_synced ? billingRules.call_out_fee_per_appointment : 0,
            chargeCallOutPerAppointment: billingRules.charge_call_out_per_appointment,
            callOutAppointmentCount: teamScopeSnapshot?.total_appointments || 1,
          })
        : [];

      if (teLines.length > 0) {
        for (const line of teLines) {
          const groupKey = line.source === 'callout_fee' ? 'fees' : 'time';
          (linesByGroup[groupKey] ??= []).push({ ...line, group_key: groupKey, group_label: groupLabels[groupKey] });
        }

        // Team-mode lines are appointment-level — keep the per-member
        // breakdown as comments so reviewers still see who contributed.
        if (timerAggregationMode === 'team' && billingSettings.bill_onsite_labour && onsiteHours > 0) {
          const byUser = (ts?.by_user as Array<{ name: string; onsite_hours: number; billable_hours: number }> | undefined) || [];
          for (const member of byUser) {
            if (member.onsite_hours > 0) {
              (linesByGroup['time'] ??= []).push({
                line_type: 'comment',
                item_name: '',
                description: `  • ${member.name}: ${member.onsite_hours.toFixed(2)} hrs on-site`,
                quantity: 0,
                unit: '',
                unit_cost: 0,
                total_cost: 0,
                line_total: 0,
                source: 'time_labour',
                group_key: 'time',
                group_label: 'Time',
              });
            }
          }
        }
      } else {
        if (billingSettings.bill_onsite_labour && onsiteHours > 0) {
          const labourRate = billingRates.labour_rate || 0;
          const labourTotal = onsiteHours * labourRate;
          (linesByGroup['time'] ??= []).push({
            line_type: 'service',
            item_name: 'On-site Labour',
            description: `On-site labour (${onsiteHours.toFixed(2)} hours)`,
            quantity: Number(onsiteHours.toFixed(2)),
            unit: 'hour',
            unit_cost: labourRate,
            total_cost: Number(labourTotal.toFixed(2)),
            tax_rate: defaultTaxRate,
            line_total: Number(labourTotal.toFixed(2)),
            source: 'time_labour',
            group_key: 'time',
            group_label: 'Time',
          });

          // Per-team-member comment lines
          const byUser = (ts?.by_user as Array<{ name: string; onsite_hours: number; billable_hours: number }> | undefined) || [];
          for (const member of byUser) {
            if (member.onsite_hours > 0) {
              (linesByGroup['time'] ??= []).push({
                line_type: 'comment',
                item_name: '',
                description: `  • ${member.name}: ${member.onsite_hours.toFixed(2)} hrs on-site`,
                quantity: 0,
                unit: '',
                unit_cost: 0,
                total_cost: 0,
                line_total: 0,
                source: 'time_labour',
                group_key: 'time',
                group_label: 'Time',
              });
            }
          }
        }

        if (billingSettings.bill_travel_time && travelHours > 0) {
          const travelTimeRate = billingRates.travel_time_rate || 0;
          const chargeableHours = billingRules.min_travel_time_hours > 0
            ? Math.max(0, travelHours - billingRules.min_travel_time_hours)
            : travelHours;
          if (chargeableHours > 0) {
            const travelTimeTotal = chargeableHours * travelTimeRate;
            (linesByGroup['time'] ??= []).push({
              line_type: 'service',
              item_name: 'Travel Time',
              description: `Travel time (${chargeableHours.toFixed(2)} hours)`,
              quantity: Number(chargeableHours.toFixed(2)),
              unit: 'hour',
              unit_cost: travelTimeRate,
              total_cost: Number(travelTimeTotal.toFixed(2)),
              tax_rate: defaultTaxRate,
              line_total: Number(travelTimeTotal.toFixed(2)),
              source: 'time_travel',
              group_key: 'time',
              group_label: 'Time',
            });
          }
        }

        if (billingSettings.bill_travel_km && travelKm > 0) {
          const travelKmRate = billingRates.travel_km_rate || 0;
          const chargeableKm = billingRules.min_travel_distance_km > 0
            ? Math.max(0, travelKm - billingRules.min_travel_distance_km)
            : travelKm;
          if (chargeableKm > 0) {
            const travelKmTotal = chargeableKm * travelKmRate;
            (linesByGroup['time'] ??= []).push({
              line_type: 'service',
              item_name: 'Travel Distance',
              description: `Travel distance (${chargeableKm.toFixed(1)} km)`,
              quantity: Number(chargeableKm.toFixed(1)),
              unit: 'km',
              unit_cost: travelKmRate,
              total_cost: Number(travelKmTotal.toFixed(2)),
              tax_rate: defaultTaxRate,
              line_total: Number(travelKmTotal.toFixed(2)),
              source: 'time_distance',
              group_key: 'time',
              group_label: 'Time',
            });
          }
        }

        // --- Call-out fee ---
        if (billingRules.is_synced && billingRules.call_out_fee_per_appointment > 0) {
          const apptCount = teamScopeSnapshot?.total_appointments || 1;
          const callOutTotal = billingRules.charge_call_out_per_appointment
            ? billingRules.call_out_fee_per_appointment * apptCount
            : billingRules.call_out_fee_per_appointment;
          (linesByGroup['fees'] ??= []).push({
            line_type: 'service',
            item_name: 'Call-out Fee',
            description: `Call-out fee (${apptCount} appointment${apptCount > 1 ? 's' : ''})`,
            quantity: billingRules.charge_call_out_per_appointment ? apptCount : 1,
            unit: 'each',
            unit_cost: billingRules.call_out_fee_per_appointment,
            total_cost: Number(callOutTotal.toFixed(2)),
            tax_rate: defaultTaxRate,
            line_total: Number(callOutTotal.toFixed(2)),
            source: 'callout_fee',
            group_key: 'fees',
            group_label: 'Fees',
          });
        }
      }

      // --- BOM lines ---
      for (const item of freshBomItems) {
        const qty = Number(item.quantity) || 0;
        const unitCost = Number(item.unit_cost) || 0;
        const totalCost = Number(item.line_total ?? item.total_cost ?? (qty * unitCost)) || 0;
        (linesByGroup['bom'] ??= []).push({
          line_type: item.line_type || 'item',
          item_name: item.item_name || item.description || 'Item',
          description: item.description || '',
          notes: item.notes,
          item_code: item.item_code ?? null,
          item_id: item.item_id ?? null,
          quantity: qty,
          unit: item.unit || 'unit',
          unit_cost: unitCost,
          total_cost: totalCost,
          tax_rate: Number(item.tax_rate) || defaultTaxRate,
          discount_percentage: Number(item.discount_percentage) || 0,
          line_total: totalCost,
          source: 'bom',
          group_key: 'bom',
          group_label: 'Materials',
        });
      }

      // --- Expense lines ---
      // DIN rows are expense-inbox intake records, not finance documents.
      // Approving a DIN materializes a linked EXP/BIL (which getJobExpenses
      // returns alongside it) — including the DIN row too would double-bill.
      // For real expense records, only rows past the approval gate are
      // billable: pending/draft/rejected/void states stay out.
      for (const expense of freshJobExpenses) {
        const expenseCf = (expense?.custom_fields || {}) as Record<string, unknown>;
        if (String(expense?.item_type || '').toLowerCase() === 'din') continue;
        const expenseStatus = String(expense?.status ?? expenseCf.expense_status ?? '').toLowerCase();
        const approvalStatus = String(expense?.approval_status ?? expenseCf.approval_status ?? '').toLowerCase();
        if (['new', 'draft', 'pending', 'pending_approval', 'pending_operations_approval',
          'rejected', 'returned', 'void', 'cancelled', 'deleted'].includes(expenseStatus)) continue;
        if (['pending', 'submitted', 'under_review', 'returned', 'escalated', 'rejected'].includes(approvalStatus)) continue;
        if (expense?.is_billable === false || expenseCf.is_billable === false) continue;
        const amount = Number(expense.grand_total ?? expense.amount ?? 0) || 0;
        if (amount <= 0) continue;
        (linesByGroup['expenses'] ??= []).push({
          line_type: 'expense',
          item_name: expense.title || expense.expense_category || 'Expense',
          description: expense.description || expense.title || '',
          quantity: 1,
          unit: 'each',
          unit_cost: amount,
          total_cost: amount,
          tax_rate: defaultTaxRate,
          line_total: amount,
          source: 'expense',
          group_key: 'expenses',
          group_label: 'Expenses',
        });
      }

      // --- Assemble generatedLines with header/subtotal structural lines ---
      for (const groupKey of groupOrder) {
        const groupLines = linesByGroup[groupKey];
        if (!groupLines || groupLines.length === 0) continue;
        const groupLabel = groupLabels[groupKey] || groupKey;
        const groupTotal = groupLines.reduce((sum, l) => sum + Number(l.line_total || 0), 0);
        generatedLines.push({
          line_type: 'header',
          item_name: groupLabel,
          description: '',
          quantity: 0,
          unit: '',
          unit_cost: 0,
          total_cost: 0,
          line_total: 0,
          group_key: groupKey,
          group_label: groupLabel,
        });
        generatedLines.push(...groupLines);
        generatedLines.push({
          line_type: 'subtotal',
          item_name: `Subtotal: ${groupLabel}`,
          description: '',
          quantity: 0,
          unit: '',
          unit_cost: 0,
          total_cost: Number(groupTotal.toFixed(2)),
          line_total: Number(groupTotal.toFixed(2)),
          group_key: groupKey,
          group_label: groupLabel,
        });
      }

      const res = await fetch(`/api/v1/work-items/${workItemId}/billing-submission`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status: 'draft',
          regenerate: true,
          requested_doc_type: requestedDocType,
          default_tax_rate: defaultTaxRate,
          line_items: generatedLines,
          billing_settings: {
            bill_travel_time: billingSettings.bill_travel_time,
            bill_travel_km: billingSettings.bill_travel_km,
            bill_onsite_labour: billingSettings.bill_onsite_labour,
          },
          timer_aggregation_mode: timerAggregationMode,
        }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        if (json.data?.status === 'draft') {
          notify.info(`Draft billing submission ${json.data.item_number} already exists - opening submissions`);
          handleSetLedgerGenerated(true);
          setActiveSection('submissions');
          if (json.data.id && onOpenDocumentModal) {
            onOpenDocumentModal(json.data.id, 'billing_submission');
          }
        } else if (res.status === 409) {
          notify.warning(json.error || 'Billing is not available for this job status', 6000);
        } else {
          throw new Error(json.error || 'Failed to create billing draft');
        }
      } else {
        notify.success(json.message || 'Draft billing submission created');
        handleSetLedgerGenerated(true);
        setActiveSection('submissions');
        const newDocId = json.data?.id;
        if (newDocId && onOpenDocumentModal) {
          onOpenDocumentModal(newDocId, 'billing_submission');
        }
      }
      void loadBillingSubmissions();
    } catch (error: unknown) {
      notify.error(error instanceof Error ? error.message : 'Failed to generate billing', 5000);
    } finally {
      setGeneratingBilling(false);
    }
  }, [workItemId, workItem, generatingBilling, canSubmitForBilling, handleSetLedgerGenerated, onOpenDocumentModal, loadBillingSubmissions, billingSettings, timerAggregationMode, timeSummary, projectTimeSummary, bomItems, jobExpenses, billingRates, billingRules, teamScopeSnapshot, defaultTaxRate, requestedDocType]);

  // Watch pendingDocType prop — when the parent (WorkItemDetailModal header dropdown) sets it,
  // update the local requestedDocType and trigger billing generation.
  useEffect(() => {
    if (!pendingDocType || pendingDocTypeRef.current === pendingDocType) return;
    pendingDocTypeRef.current = pendingDocType;
    setRequestedDocType(pendingDocType);
    setActiveSection('submissions');
    void handleGenerateBilling();
  }, [pendingDocType, handleGenerateBilling]);

  const [overviewLoading, setOverviewLoading] = useState(sectionNeedsOverview(initialSection));
  const [projectExpenseSummary, setProjectExpenseSummary] = useState<ProjectExpenseSummary | null>(null);
  const [maintenanceRecords, setMaintenanceRecords] = useState<MaintenanceRecord[]>([]);
  const [jobFinancialInsights, setJobFinancialInsights] = useState<JobFinancialInsights | null>(null);

  const [showBillingRulesPanel, setShowBillingRulesPanel] = useState(false);
  const [hydratedWorkItem, setHydratedWorkItem] = useState<Partial<BillingWorkspaceItem> | null>(null);

  useEffect(() => {
    saveTimerAggregationMode(timerAggregationMode);
  }, [timerAggregationMode]);
  const timeComposerToken = 0;
  const expenseComposerToken = 0;

  const resolvedWorkItem = useMemo<BillingWorkspaceItem | undefined>(() => {
    if (workItem) {
      return {
        ...hydratedWorkItem,
        ...workItem,
      };
    }

    if (!workItemId) {
      return undefined;
    }

    return {
      id: workItemId,
      item_type: workItemType,
      title,
      ...hydratedWorkItem,
    };
  }, [hydratedWorkItem, title, workItem, workItemId, workItemType]);

  // ── Timer aggregation mode: per-item persistence ─────────────
  // The toggle is a per-work-item billing setting. Resolution order:
  //   1. work item's own custom_fields.timer_aggregation_mode
  //   2. latest billing submission's stored mode (legacy items)
  //   3. browser-global localStorage default (initial state above)
  // An explicit toggle in this session wins over late-arriving hydration.
  const timerModeUserToggledRef = useRef(false);
  const timerModeHydratedKeyRef = useRef<string | null>(null);
  const storedTimerAggregationMode = getTimerAggregationModeFromCustomFields(resolvedWorkItem?.custom_fields)
    ?? normalizeTimerAggregationMode(
      billingSubmissions.find((sub) => sub.timer_aggregation_mode)?.timer_aggregation_mode,
    );

  useEffect(() => {
    timerModeUserToggledRef.current = false;
    timerModeHydratedKeyRef.current = null;
  }, [workItemId]);

  useEffect(() => {
    if (!workItemId || !storedTimerAggregationMode || timerModeUserToggledRef.current) return;
    const hydrationKey = `${workItemId}:${storedTimerAggregationMode}`;
    if (timerModeHydratedKeyRef.current === hydrationKey) return;
    timerModeHydratedKeyRef.current = hydrationKey;
    setTimerAggregationMode((current) => current === storedTimerAggregationMode ? current : storedTimerAggregationMode);
  }, [workItemId, storedTimerAggregationMode]);

  const handleTimerAggregationModeChange = useCallback((mode: 'individual' | 'team') => {
    timerModeUserToggledRef.current = true;
    setTimerAggregationMode(mode);
    // Persist onto the work item so reports (job cards, emailed PDFs, public
    // API documents) and other sessions resolve the same per-item setting.
    persistTimerAggregationModeForWorkItem(workItemId, mode);
    onTimerAggregationModeChange?.(mode);
  }, [workItemId, onTimerAggregationModeChange]);

  useEffect(() => {
    if (!workItemId) {
      setHydratedWorkItem(null);
      return;
    }

    const alreadyHydrated = Boolean(
      workItem?.contact_id != null
      && workItem?.contact_name
      && workItem?.status
      && workItem?.item_number,
    );

    if (alreadyHydrated) {
      setHydratedWorkItem(null);
      return;
    }

    let cancelled = false;

    const hydrateWorkItemContext = async () => {
      try {
        const payload = await fetchJson<WorkItemApiResponse>(`/api/v1/work-items/${workItemId}?lite=true`, {
          headers: getAuthHeaders(),
        });

        if (!payload?.success || !payload.data || cancelled) {
          return;
        }

        const item = payload.data;
        setHydratedWorkItem({
          id: item.id || workItemId,
          item_number: item.item_number,
          title: item.title,
          item_type: item.item_type,
          item_subtype: item.item_subtype,
          contact_id: item.contact_id ?? undefined,
          contact_name: item.contact_name ?? undefined,
          status: item.status ?? undefined,
          assigned_to: item.assigned_to ?? undefined,
          custom_fields: item.custom_fields ?? undefined,
          location_id: item.location_id ?? undefined,
          location_name: item.location_name ?? undefined,
          location_address: item.location_address ?? undefined,
          contact_person_id: item.contact_person_id ?? undefined,
          contact_person_name: item.contact_person_name ?? undefined,
          assignees: Array.isArray(item.assigned_users_details)
            ? item.assigned_users_details.map((assignee) => ({
              user_id: assignee.user_id ?? undefined,
              first_name: assignee.first_name ?? undefined,
              last_name: assignee.last_name ?? undefined,
              email: assignee.email ?? undefined,
            }))
            : undefined,
        });
      } catch (error) {
        console.error('[BillingDashboard] Failed to hydrate work item context:', error);
      }
    };

    void hydrateWorkItemContext();

    return () => {
      cancelled = true;
    };
  }, [
    workItem?.contact_id,
    workItem?.contact_name,
    workItem?.id,
    workItem?.item_number,
    workItem?.status,
    workItemId,
  ]);

  const projectTabWorkItem = useMemo<WorkItem | undefined>(() => {
    if (!resolvedWorkItem) {
      return undefined;
    }

    const normalizedAssignedTo = typeof resolvedWorkItem.assigned_to === 'number'
      ? resolvedWorkItem.assigned_to
      : typeof resolvedWorkItem.assigned_to === 'string' && resolvedWorkItem.assigned_to.trim()
        ? Number(resolvedWorkItem.assigned_to)
        : undefined;

    const normalizedAssignees = Array.isArray(resolvedWorkItem.assignees)
      ? resolvedWorkItem.assignees.flatMap((assignee) => {
        const normalizedUserId = typeof assignee.user_id === 'number'
          ? assignee.user_id
          : typeof assignee.user_id === 'string' && assignee.user_id.trim()
            ? Number(assignee.user_id)
            : Number.NaN;

        if (!Number.isFinite(normalizedUserId)) {
          return [];
        }

        return [{
          user_id: normalizedUserId,
          first_name: assignee.first_name ?? undefined,
          last_name: assignee.last_name ?? undefined,
          email: assignee.email ?? undefined,
        }];
      })
      : undefined;

    return {
      id: resolvedWorkItem.id,
      item_number: resolvedWorkItem.item_number,
      title: resolvedWorkItem.title,
      item_type: resolvedWorkItem.item_type || 'project',
      item_subtype: resolvedWorkItem.item_subtype,
      contact_id: resolvedWorkItem.contact_id,
      contact_name: resolvedWorkItem.contact_name,
      status: resolvedWorkItem.status,
      assigned_to: Number.isFinite(normalizedAssignedTo) ? normalizedAssignedTo : undefined,
      custom_fields: resolvedWorkItem.custom_fields,
      assignees: normalizedAssignees,
    } as WorkItem;
  }, [resolvedWorkItem]);

  const explicitRootIds = useMemo(() => normalizeIds(rootItemIds), [rootItemIds]);

  const resolvedRootItemIds = useMemo(() => {
    if (explicitRootIds.length > 0) {
      return explicitRootIds;
    }

    if (workItemId) {
      return [workItemId];
    }

    return [];
  }, [explicitRootIds, workItemId]);

  const resolvedRootItemIdsKey = useMemo(
    () => resolvedRootItemIds.join(','),
    [resolvedRootItemIds],
  );

  const resolvedWorkItemType = resolvedWorkItem?.item_type || workItemType;
  const isJobWorkspace = resolvedWorkItemType === 'job';
  const { workflowStatuses: jobWorkflowStatuses } = useJobConfig({
    itemType: 'job',
    skip: !isJobWorkspace,
  });
  const isProject = resolvedWorkItemType === 'project';
  const isAsset = resolvedWorkItemType === 'asset';
  const showMaintenance = isAsset;
  const showExpenses = Boolean(resolvedWorkItemType) && resolvedWorkItemType !== 'inventory';
  const showDetailedWorkspace = Boolean(workItemId && resolvedWorkItemType);
  const multipleRoots = resolvedRootItemIds.length > 1;

  const effectiveJobStatus = useMemo<EffectiveJobStatusDetails>(() => {
    if (!isJobWorkspace) {
      return {
        status: resolvedWorkItem?.status,
        sourceStatus: resolvedWorkItem?.status,
        appointmentCount: 0,
        activeAppointmentCount: 0,
        derivedFromAppointments: false,
      };
    }

    return deriveEffectiveJobStatus(resolvedWorkItem?.status, jobFinancialInsights?.source_breakdown);
  }, [isJobWorkspace, jobFinancialInsights?.source_breakdown, resolvedWorkItem?.status]);

  const billingStatusWorkItem = useMemo<BillingWorkspaceItem | undefined>(() => {
    if (!resolvedWorkItem) {
      return undefined;
    }

    if (!isJobWorkspace) {
      return resolvedWorkItem;
    }

    const existingCustomFields = resolvedWorkItem.custom_fields && typeof resolvedWorkItem.custom_fields === 'object'
      ? resolvedWorkItem.custom_fields
      : {};

    return {
      ...resolvedWorkItem,
      status: effectiveJobStatus.status ?? resolvedWorkItem.status,
      custom_fields: {
        ...existingCustomFields,
        child_appointment_count: effectiveJobStatus.appointmentCount,
        child_active_appointment_count: effectiveJobStatus.activeAppointmentCount,
        effective_status_source: effectiveJobStatus.derivedFromAppointments ? 'appointments' : 'job',
        source_status: effectiveJobStatus.sourceStatus ?? resolvedWorkItem.status,
      },
    };
  }, [effectiveJobStatus, isJobWorkspace, resolvedWorkItem]);

  const canAccessJobBillableLedger = useMemo(() => (
    !isJobWorkspace || canShowJobBillableLedgerForStatus(billingStatusWorkItem?.status, jobWorkflowStatuses)
  ), [billingStatusWorkItem?.status, isJobWorkspace, jobWorkflowStatuses]);

  const isClosedJobBillingReadOnly = useMemo(() => (
    isJobWorkspace && isReadOnlyJobBillingStatus(billingStatusWorkItem?.status)
  ), [billingStatusWorkItem?.status, isJobWorkspace]);

  const billingWorkspaceReadOnly = readOnly || isClosedJobBillingReadOnly;

  const billingLocked = useMemo(() => {
    if (!isJobWorkspace) return false;
    const status = billingStatusWorkItem?.status;
    return status === 'closed';
  }, [billingStatusWorkItem?.status, isJobWorkspace]);

  const relatedTimeTrackingIds = useMemo(() => {
    const ids = [...resolvedRootItemIds];

    if (isJobWorkspace && Array.isArray(jobFinancialInsights?.source_breakdown)) {
      jobFinancialInsights.source_breakdown.forEach((source) => {
        if (source?.id) {
          ids.push(String(source.id));
        }
      });
    }

    return Array.from(new Set(ids.filter(Boolean)));
  }, [isJobWorkspace, jobFinancialInsights?.source_breakdown, resolvedRootItemIds]);

  const resolvedItemTypes = useMemo(() => {
    if (itemTypes) {
      return itemTypes;
    }

    if (resolvedWorkItemType) {
      return resolvedWorkItemType;
    }

    return 'job,project';
  }, [itemTypes, resolvedWorkItemType]);

  const resolvedIncludeDescendants = useMemo(() => {
    if (typeof includeDescendants === 'boolean') {
      return includeDescendants;
    }

    if (resolvedWorkItemType === 'job' || resolvedWorkItemType === 'project' || resolvedWorkItemType === 'task') {
      return true;
    }

    // Module-level dashboards have no resolvedWorkItemType — the scope is the
    // itemTypes filter. Include descendants so job/project roots roll up their
    // appointment/task children (costs, timers, assignees).
    if (resolvedWorkItemType == null) {
      return true;
    }

    return false;
  }, [includeDescendants, resolvedWorkItemType]);

  const scopeType = useMemo(
    () => getScopeType(resolvedWorkItemType, resolvedRootItemIds.length > 0),
    [resolvedRootItemIds.length, resolvedWorkItemType],
  );
  const canEditBillingTimers = timersSignoffAccess;

  const resolvedTitle = useMemo(() => title || 'Billing', [title]);

  const jobCostingConfig = useMemo(() => {
    // For service-operations: hide outsourcing, use direct billing integration
    // For manufacturing: show outsourcing, use billing-tab integration
    const isServiceOperations = templateKey === 'service-operations';
    const isManufacturing = templateKey === 'advanced-manufacturing' || templateKey?.includes('manufacturing');

    return {
      showOutsourcing: isManufacturing && !isServiceOperations,
      billingIntegrationMode: isServiceOperations ? 'direct' as const : 'billing-tab' as const,
    };
  }, [templateKey]);

  const resolvedSubtitle = useMemo(() => {
    if (subtitle) {
      return subtitle;
    }

    if (scopeType === 'appointment') {
      return 'Track timers, expenses, inventory, sundries, and profit for this appointment.';
    }

    if (scopeType === 'task') {
      return 'Track operational effort, recoverables, expenses, and costing for this task.';
    }

    if (scopeType === 'project') {
      return 'Track combined billing across this project and its linked jobs and appointments.';
    }

    if (scopeType === 'job') {
      return 'Track combined billing across this job and every linked appointment and work item.';
    }

    return 'Track billing, costs, timers, expenses, and profitability across the current operational scope.';
  }, [scopeType, subtitle]);

  const sections = useMemo(() => {
    if (isJobWorkspace) {
      const timeSection = {
        id: 'time' as BillingSectionId,
        label: 'Timers',
        description: 'Consolidated SmartTask time, travel, and labour capture across every linked appointment and assignee on this job.',
        icon: Clock3,
      };
      const costingSection = {
        id: 'costing' as BillingSectionId,
        label: 'Dashboard',
        description: 'Financial health, costing, profitability, team productivity, and linked finance documents for this job workspace.',
        icon: DollarSign,
      };

      if (jobBillingWorkspaceAccess === false) {
        return [timeSection];
      }

      return [timeSection, costingSection];
    }

    const nextSections: Array<{
      id: BillingSectionId;
      label: string;
      description: string;
      icon: React.ElementType;
    }> = [];

    nextSections.push(
      {
        id: 'costing',
        label: 'Costing dashboard',
        description: 'Combined costing, margin, labour, travel, and profitability view for the current billing scope.',
        icon: DollarSign,
      },
      {
        id: 'time',
        label: 'Hours & timers',
        description: 'SmartTask timers, travel time, travel distance, in-progress time, and manual time capture.',
        icon: Clock3,
      },
    );

    if (showMaintenance) {
      nextSections.push({
        id: 'maintenance',
        label: 'Maintenance',
        description: 'Maintenance-linked operational and commercial context for asset work.',
        icon: Wrench,
      });
    }

    return nextSections;
  }, [isJobWorkspace, showMaintenance, jobBillingWorkspaceAccess]);

  useEffect(() => {
    if (activeSection === 'expenses' || activeSection === 'submissions' || activeSection === 'ledger') {
      setActiveSection('costing');
      return;
    }
    if (!sections.some((section) => section.id === activeSection)) {
      setActiveSection(sections[0]?.id || 'costing');
    }
  }, [activeSection, sections]);

  useEffect(() => {
    if (!isJobWorkspace || jobBillingWorkspaceAccess !== false) {
      return;
    }
    if (['ledger', 'submissions', 'materials', 'expenses', 'costing'].includes(activeSection)) {
      setActiveSection('time');
    }
  }, [activeSection, isJobWorkspace, jobBillingWorkspaceAccess]); // keep 'submissions' in guard for backward compat

  useEffect(() => {
    if ((activeSection as string) === 'materials' || (activeSection as string) === 'ledger') {
      setActiveSection('costing');
    }
  }, [activeSection]);

  // Only sync activeSection from parent's defaultSection when the prop itself
  // changes — not when `sections` identity churns (e.g. status/flags loading).
  // This prevents the parent-level tab strip from stomping the user's
  // internal section click mid-render.
  useEffect(() => {
    if (!defaultSection) {
      return;
    }

    if (appliedDefaultSectionRef.current === defaultSection) {
      return;
    }

    if (sections.some((section) => section.id === defaultSection)) {
      appliedDefaultSectionRef.current = defaultSection;
      setActiveSection(defaultSection);
    }
  }, [defaultSection, sections]);

  useEffect(() => {
    hasLoadedRef.current = false;
    setOverviewLoading(sectionNeedsOverview(initialSection));
    setTimeSummary(null);
    setProjectTimeSummary(null);
    setBomItems([]);
    setJobExpenses([]);
    setProjectExpenseSummary(null);
    setMaintenanceRecords([]);
    setJobFinancialInsights(null);
    setTeamScopeSnapshot(null);
    setBillingRates({
      labour_rate: 0,
      travel_time_rate: 0,
      travel_km_rate: 0,
      seeded: false,
    });
  }, [initialSection, workItemId]);

  const loadOverview = useCallback(async () => {
    if (!showDetailedWorkspace) {
      setOverviewLoading(false);
      return;
    }

    setOverviewLoading(true);
    try {
      const headers = getAuthHeaders();
      const targetIds = resolvedRootItemIds.length > 0
        ? resolvedRootItemIds
        : (workItemId ? [workItemId] : []);

      const effectiveIncludeRelated = !multipleRoots && (includeRelated ?? (resolvedWorkItemType !== 'appointment'));
      const relatedQuery = effectiveIncludeRelated ? '?include_related=true' : '';

      const teamScopeResponsePromise = (() => {
        if (resolvedWorkItemType === 'job' && targetIds.length === 1) {
          return fetchJson<{
            success?: boolean;
            data?: {
              source_breakdown?: Array<{
                id?: string | null;
                item_number?: string | null;
                item_type?: string;
                assigned_team_name?: string | null;
              }>;
            };
          }>(`/api/v1/work-items/${targetIds[0]}/financial-summary`, { headers });
        }

        const appointmentScopeTargetId = resolvedWorkItemType === 'appointment' && targetIds.length === 1 ? targetIds[0] : null;

        if (appointmentScopeTargetId) {
          return fetchJson<{
            success?: boolean;
            appointment_breakdown?: Array<{
              source_work_item_id?: string | null;
              item_number?: string | null;
              assigned_team_name?: string | null;
            }>;
          }>(`/api/v1/work-items/${appointmentScopeTargetId}/time-entries?include_related=true`, { headers });
        }

        return Promise.resolve(null);
      })();

      // When team aggregation is selected, fetch raw time entries alongside the summary
      // so we can override onsite/travel hours with team-merged (non-overlapping) values.
      const teamRawEntriesFetch = timerAggregationMode === 'team' && !(isProject && targetIds.length === 1)
        ? pMap(
            targetIds,
            (targetId) =>
              fetchJson<{ data?: Array<Record<string, unknown>> }>(
                `/api/v1/work-items/${targetId}/time-entries${multipleRoots ? '' : relatedQuery}`,
                { headers },
              ).then((data) => (Array.isArray(data?.data) ? data.data : [])),
          )
        : Promise.resolve([] as Array<Array<Record<string, unknown>>>);

      const [bomResponses, timeResponses, expenseResponses, maintenanceResponse, billingRatesResponse, teamScopeResponse, teamRawEntriesResponses] = await Promise.all([
        pMap(
          targetIds,
          async (targetId) => {
            const data = await fetchJson<{ data?: { items?: BomItem[] } | BomItem[] }>(
              `/api/v1/work-items/${targetId}/bom${multipleRoots ? '' : relatedQuery}`,
              { headers },
            );
            if (Array.isArray(data?.data)) {
              return data.data;
            }
            if (Array.isArray(data?.data?.items)) {
              return data.data.items;
            }
            return [] as BomItem[];
          },
        ),
        isProject && targetIds.length === 1
          ? Promise.resolve([] as Array<TimeSummary | ProjectTimeSummary | null>)
          : pMap(
            targetIds,
            (targetId) =>
              fetchJson<{ success?: boolean; summary?: TimeSummary }>(
                `/api/v1/work-items/${targetId}/time-entries/summary${multipleRoots ? '' : relatedQuery}`,
                { headers },
              ).then((data) => (data?.success ? data.summary || null : null)),
          ),
        showExpenses
          ? pMap(
            targetIds,
            (targetId) =>
              fetchJson<{ success?: boolean; expenses?: JobExpense[]; data?: JobExpense[] }>(
                '/api/v1/work-items/fleet',
                {
                  method: 'POST',
                  headers,
                  body: JSON.stringify({
                    action: 'getJobExpenses',
                    jobId: targetId,
                    include_related: multipleRoots ? false : effectiveIncludeRelated,
                  }),
                },
              ).then((data) => {
                if (!data?.success) {
                  return [] as JobExpense[];
                }
                return Array.isArray(data.expenses)
                  ? data.expenses
                  : Array.isArray(data.data)
                    ? data.data
                    : [];
              }),
          )
          : Promise.resolve([] as JobExpense[][]),
        showMaintenance && targetIds.length === 1
          ? fetchJson<{ success?: boolean; data?: MaintenanceRecord[] | { records?: MaintenanceRecord[] } }>(
            `/api/v1/work-items/${targetIds[0]}/maintenance?limit=50`,
            { headers },
          )
          : Promise.resolve(null),
        fetchJson<{
          success?: boolean;
          seeded?: boolean;
          data?: {
            labour_rate?: number;
            travel_time_rate?: number;
            travel_km_rate?: number;
          };
        }>('/api/v1/work-items/billing-rates', { headers }),
        teamScopeResponsePromise,
        teamRawEntriesFetch,
      ]);

      if (isProject && targetIds.length === 1) {
        const [projectTimeData, projectExpenseData] = await Promise.all([
          fetchJson<{ success?: boolean; data?: { summary?: ProjectTimeSummary } }>(
            `/api/v1/projects/${targetIds[0]}/time-entries`,
            { headers },
          ),
          fetchJson<{ success?: boolean; data?: { summary?: ProjectExpenseSummary } }>(
            `/api/v1/projects/${targetIds[0]}/expenses`,
            { headers },
          ),
        ]);

        setProjectTimeSummary(projectTimeData?.success ? projectTimeData.data?.summary || null : null);
        setProjectExpenseSummary(projectExpenseData?.success ? projectExpenseData.data?.summary || null : null);
        setJobFinancialInsights(null);
      } else {
        const mergedTimeSummary = timeResponses.reduce<TimeSummary | null>((aggregate, summary) => {
          if (!summary || 'total_hours' in summary) {
            return aggregate;
          }

          if (!aggregate) {
            return { ...summary };
          }

          return {
            total_entries: aggregate.total_entries + (summary.total_entries || 0),
            total_duration_hours: aggregate.total_duration_hours + (summary.total_duration_hours || 0),
            billable_duration_hours: aggregate.billable_duration_hours + (summary.billable_duration_hours || 0),
            non_billable_duration_hours: aggregate.non_billable_duration_hours + (summary.non_billable_duration_hours || 0),
            billable_amount: (aggregate.billable_amount || 0) + (summary.billable_amount || 0),
            total_hours_formatted: '',
            related_items_count: (aggregate.related_items_count || 0) + (summary.related_items_count || 0),
            travel_hours: aggregate.travel_hours + (summary.travel_hours || 0),
            travel_distance_km: aggregate.travel_distance_km + (summary.travel_distance_km || 0),
            onsite_hours: aggregate.onsite_hours + (summary.onsite_hours || 0),
          };
        }, null);

        // When team aggregation is selected, override onsite/travel hours with team-merged values
        let finalTimeSummary = mergedTimeSummary;
        if (timerAggregationMode === 'team' && finalTimeSummary && teamRawEntriesResponses.length > 0) {
          const allRawEntries = teamRawEntriesResponses.flat();
          if (allRawEntries.length > 0) {
            const teamValues = computeTeamAggregatedHours(allRawEntries);
            finalTimeSummary = {
              ...finalTimeSummary,
              onsite_hours: teamValues.onsiteHours,
              travel_hours: teamValues.travelHours,
            };
          }
        }
        setTimeSummary(finalTimeSummary);
        setProjectTimeSummary(null);

        if (resolvedWorkItemType === 'job' && targetIds.length === 1) {
          const financialSummary = teamScopeResponse as {
            success?: boolean;
            data?: JobFinancialInsights;
          } | null;
          setJobFinancialInsights(financialSummary?.success && financialSummary.data ? sanitizeJobFinancialInsights(financialSummary.data) : null);
        } else {
          setJobFinancialInsights(null);
        }
      }

      setBomItems(dedupeRecords(bomResponses.flat(), (item, index) => item.id || `bom:${index}`));
      const mergedExpenses = dedupeRecords(expenseResponses.flat(), (expense, index) => expense.id || `expense:${index}`);
      setJobExpenses(mergedExpenses);
      onExpensesChange?.(mergedExpenses.length);

      if (maintenanceResponse?.success) {
        const nextRecords = dedupeRecords(
          Array.isArray(maintenanceResponse.data)
            ? maintenanceResponse.data
            : Array.isArray(maintenanceResponse.data?.records)
              ? maintenanceResponse.data.records
              : [],
          (record, index) => record.id || `${record.next_due_date || ''}:${index}`,
        );
        setMaintenanceRecords(nextRecords);
      } else {
        setMaintenanceRecords([]);
      }

      if (billingRatesResponse?.success && billingRatesResponse.data) {
        setBillingRates({
          labour_rate: Number(billingRatesResponse.data.labour_rate || 0),
          travel_time_rate: Number(billingRatesResponse.data.travel_time_rate || 0),
          travel_km_rate: Number(billingRatesResponse.data.travel_km_rate || 0),
          seeded: billingRatesResponse.seeded === true,
        });
      }

      // Load billing settings from finance settings (localStorage overrides take precedence)
      try {
        const settingsResponse = await fetch('/api/v1/finance/settings', { headers });
        if (settingsResponse.ok) {
          const settingsData = await settingsResponse.json();
          if (settingsData.data?.custom_settings?.billing) {
            const bs = settingsData.data.custom_settings.billing;
            const stored = loadChargeTogglesFromStorage();
            setBillingSettings({
              bill_travel_time: stored ? stored.travel_time : (bs.bill_travel_time !== false),
              bill_travel_km: stored ? stored.travel_km : (bs.bill_travel_km !== false),
              bill_onsite_labour: stored ? stored.onsite_labour : (bs.bill_onsite_labour !== false),
            });
          }
          if (settingsData.data?.default_tax_rate != null) {
            setDefaultTaxRate(Number(settingsData.data.default_tax_rate) || 0);
          }
        }
      } catch (err) {
        console.error('Failed to load billing settings:', err);
      }

      if (resolvedWorkItemType === 'job') {
        setTeamScopeSnapshot(summarizeTeamScopeFromFinancialSummary(teamScopeResponse as {
          success?: boolean;
          data?: {
            source_breakdown?: Array<{
              id?: string | null;
              item_number?: string | null;
              item_type?: string;
              assigned_team_name?: string | null;
            }>;
          };
        } | null));
      } else {
        setTeamScopeSnapshot(summarizeTeamScopeFromTimeEntries(teamScopeResponse as {
          success?: boolean;
          appointment_breakdown?: Array<{
            source_work_item_id?: string | null;
            item_number?: string | null;
            assigned_team_name?: string | null;
          }>;
        } | null));
      }
    } catch (error) {
      console.error('[BillingDashboard] Failed to load overview data:', error);
    } finally {
      setOverviewLoading(false);
    }
  }, [
    includeRelated,
    isProject,
    multipleRoots,
    onExpensesChange,
    resolvedRootItemIds,
    resolvedWorkItemType,
    setBillingSettings,
    showDetailedWorkspace,
    showExpenses,
    showMaintenance,
    timerAggregationMode,
    workItemId,
  ]);

  // Store the latest loadOverview function in the ref
  useEffect(() => {
    loadOverviewRef.current = loadOverview;
  }, [loadOverview]);

  useEffect(() => {
    if (!showDetailedWorkspace || !isActive || !sectionNeedsOverview(activeSection)) {
      return;
    }

    if (!hasLoadedRef.current) {
      void loadOverviewRef.current?.();
      hasLoadedRef.current = true;
    }
  }, [activeSection, isActive, showDetailedWorkspace, workItemId]);

  useEffect(() => {
    if (!showDetailedWorkspace || !isActive) return;
    if (activeSection === 'submissions' || isJobWorkspace) {
      void loadBillingSubmissions();
    }
  }, [activeSection, isActive, showDetailedWorkspace, isJobWorkspace, loadBillingSubmissions, workItemId]);


  useEffect(() => {
    if (!showDetailedWorkspace || !isActive || !sectionNeedsOverview(activeSection)) {
      return;
    }

    const refreshOverview = (event?: Event) => {
      const detail = (event as CustomEvent<{ workItemId?: string; workItemIds?: string[] }>)?.detail;
      const trackedIds = relatedTimeTrackingIds.length > 0 ? relatedTimeTrackingIds : resolvedRootItemIds;

      // For BOM changes on job dashboards, always refresh because appointments
      // may have changed their BOM and we aggregate them via include_related
      const isBomEvent = event?.type === 'workItemBomChanged';
      if (isBomEvent && (resolvedWorkItemType === 'job' || resolvedWorkItemType === 'project')) {
        void loadOverviewRef.current?.();
        return;
      }

      const detailWorkItemIds = Array.from(new Set([
        ...(detail?.workItemId ? [String(detail.workItemId)] : []),
        ...((detail?.workItemIds || []).map((value) => String(value || '').trim()).filter(Boolean)),
      ]));

      if (
        detailWorkItemIds.length > 0
        && trackedIds.length > 0
        && !detailWorkItemIds.some((id) => trackedIds.includes(id))
      ) {
        return;
      }
      void loadOverviewRef.current?.();
    };

    window.addEventListener('workItemStatusChange', refreshOverview);
    window.addEventListener('workItemTimeEntriesChanged', refreshOverview as EventListener);
    window.addEventListener('workItemBomChanged', refreshOverview as EventListener);

    return () => {
      window.removeEventListener('workItemStatusChange', refreshOverview);
      window.removeEventListener('workItemTimeEntriesChanged', refreshOverview as EventListener);
      window.removeEventListener('workItemBomChanged', refreshOverview as EventListener);
    };
  }, [activeSection, isActive, relatedTimeTrackingIds, resolvedRootItemIds, resolvedWorkItemType, showDetailedWorkspace]);

  const {
    billableHours,
    totalHours,
    travelHours,
    travelKm,
    onsiteHours,
    relatedItems,
    billableLinesTotal,
    expensesTotal,
    maintenanceTotal,
    effectiveLabourRate,
    labourRecoveryEstimate,
    travelTimeRecoveryEstimate,
    travelDistanceRecoveryEstimate,
    travelRecoveryEstimate,
    callOutFeeTotal,
    recoverablesTotal,
  } = useBillingCalculations({
    bomItems,
    jobExpenses,
    projectExpenseSummary,
    maintenanceRecords,
    billingRates,
    billingRules,
    timeSummary,
    projectTimeSummary,
    teamScopeSnapshot,
  });

  const quickActionCards = useMemo(() => {
    if (!billingRules.is_synced) {
      return [
        {
          label: 'Labour recovery',
          value: '—',
          icon: Clock3,
          description: 'Sync billing to calculate',
        },
        {
          label: 'Travel time',
          value: '—',
          icon: Navigation,
          description: billingRules.min_travel_time_hours > 0
            ? `Min ${billingRules.min_travel_time_hours}h before charging`
            : 'Sync billing to calculate',
        },
        {
          label: 'Travel distance',
          value: '—',
          icon: Navigation,
          description: billingRules.min_travel_distance_km > 0
            ? `Min ${billingRules.min_travel_distance_km}km before charging`
            : 'Sync billing to calculate',
        },
        {
          label: 'Call-out fees',
          value: '—',
          icon: Receipt,
          description: billingRules.call_out_fee_per_appointment > 0
            ? `R ${billingRules.call_out_fee_per_appointment.toFixed(2)} per appointment`
            : 'Sync billing to calculate',
        },
      ];
    }
    return [
      {
        label: 'Labour recovery',
        value: formatCurrency(labourRecoveryEstimate),
        icon: Clock3,
        description: `${formatHours(onsiteHours)} onsite @ ${formatCurrency(billingRates.labour_rate)}/h`,
      },
      {
        label: 'Travel time',
        value: formatCurrency(travelTimeRecoveryEstimate),
        icon: Navigation,
        description: billingRules.min_travel_time_hours > 0
          ? `${formatHours(travelHours)} travel (min ${billingRules.min_travel_time_hours}h free) @ ${formatCurrency(billingRates.travel_time_rate)}/h`
          : `${formatHours(travelHours)} travel @ ${formatCurrency(billingRates.travel_time_rate)}/h`,
      },
      {
        label: 'Travel distance',
        value: formatCurrency(travelDistanceRecoveryEstimate),
        icon: Navigation,
        description: billingRules.min_travel_distance_km > 0
          ? `${travelKm.toFixed(1)} km (min ${billingRules.min_travel_distance_km}km free) @ ${formatCurrency(billingRates.travel_km_rate)}/km`
          : `${travelKm.toFixed(1)} km @ ${formatCurrency(billingRates.travel_km_rate)}/km`,
      },
      {
        label: callOutFeeTotal > 0 ? 'Call-out fees' : 'Recoverables live',
        value: callOutFeeTotal > 0 ? formatCurrency(callOutFeeTotal) : formatCurrency(recoverablesTotal),
        icon: Receipt,
        description: callOutFeeTotal > 0
          ? `${teamScopeSnapshot?.total_appointments || 1} appointment${(teamScopeSnapshot?.total_appointments || 1) > 1 ? 's' : ''} @ R ${billingRules.call_out_fee_per_appointment.toFixed(2)}`
          : `${bomItems.length} lines · ${jobExpenses.length} expenses${showMaintenance ? ` · ${maintenanceRecords.length} maintenance` : ''}`,
      },
    ];
  }, [
    billingRules.is_synced,
    billingRules.min_travel_time_hours,
    billingRules.min_travel_distance_km,
    billingRules.call_out_fee_per_appointment,
    billingRates.labour_rate,
    billingRates.travel_km_rate,
    billingRates.travel_time_rate,
    bomItems.length,
    callOutFeeTotal,
    jobExpenses.length,
    labourRecoveryEstimate,
    maintenanceRecords.length,
    onsiteHours,
    recoverablesTotal,
    showMaintenance,
    teamScopeSnapshot?.total_appointments,
    travelDistanceRecoveryEstimate,
    travelHours,
    travelKm,
    travelTimeRecoveryEstimate,
  ]);

  const currentSection = sections.find((section) => section.id === activeSection) || sections[0];
  // When embedded in WorkItemDetailModal with showHeaderControls=false, the parent renders the
  // section tabs above us — so we suppress all internal navigation to avoid duplication.
  const hideInternalNavigation = embedded && !showHeaderControls;
  const jobPrimarySections = useMemo(() => sections as Array<{
    id: JobBillingPrimarySectionId;
    label: string;
    description: string;
    icon: React.ElementType;
  }>, [sections]);

  const budgetHealth = useMemo(() => {
    if (!jobFinancialInsights?.health) {
      return null;
    }

    return budgetHealthMeta[jobFinancialInsights.health] || {
      dot: 'bg-gray-400',
      label: String(jobFinancialInsights.health || 'Unknown'),
      pillClass: 'border-gray-200 bg-gray-50 text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300',
    };
  }, [jobFinancialInsights?.health]);

  const financialHealthCards = useMemo(() => {
    if (!jobFinancialInsights) {
      return [] as Array<{
        label: string;
        value: string;
        detail: string;
        icon: React.ElementType;
        valueClassName?: string;
      }>;
    }

    const budgetVariance = jobFinancialInsights.profitability.budget_variance;
    const netProfitPositive = jobFinancialInsights.profitability.net_profit >= 0;

    return [
      {
        label: 'Recoverables',
        value: formatCurrency(jobFinancialInsights.profitability.total_revenue),
        detail: `Current commercial value prepared for finance review`,
        icon: DollarSign,
      },
      {
        label: 'Spend to date',
        value: formatCurrency(jobFinancialInsights.profitability.total_expenses),
        detail: `Captured costs and expenses currently recorded on this job`,
        icon: Receipt,
      },
      {
        label: 'Budget variance',
        value: formatCurrency(Math.abs(budgetVariance)),
        detail: `${jobFinancialInsights.profitability.budget_variance_pct.toFixed(1)}% ${budgetVariance >= 0 ? 'over budget' : 'under budget'}`,
        icon: AlertCircle,
        valueClassName: budgetVariance >= 0
          ? (isDark ? 'text-red-300' : 'text-red-700')
          : (isDark ? 'text-emerald-300' : 'text-emerald-700'),
      },
      {
        label: 'Net position',
        value: formatCurrency(jobFinancialInsights.profitability.net_profit),
        detail: `${jobFinancialInsights.profitability.net_margin_pct.toFixed(1)}% margin on current billing vs spend`,
        icon: budgetVariance >= 0 ? AlertCircle : DollarSign,
        valueClassName: netProfitPositive
          ? (isDark ? 'text-emerald-300' : 'text-emerald-700')
          : (isDark ? 'text-red-300' : 'text-red-700'),
      },
    ];
  }, [isDark, jobFinancialInsights]);

  const sectionMetrics: Record<BillingSectionId, { value: string; detail: string }> = {
    ledger: {
      value: 'Finance handoff',
      detail: 'Review the billable ledger, then submit the billing pack to finance',
    },
    costing: {
      value: multipleRoots ? `${resolvedRootItemIds.length} items` : (resolvedWorkItem?.item_number || 'Live'),
      detail: 'Commercial summary, labour, margin, and profitability',
    },
    time: {
      value: formatHours(billableHours),
      detail: `${formatHours(onsiteHours)} onsite · ${travelKm.toFixed(1)} km travel consolidated`,
    },
    expenses: {
      value: formatCurrency(expensesTotal),
      detail: isProject
        ? `${projectExpenseSummary?.expense_count || 0} captured expense${(projectExpenseSummary?.expense_count || 0) === 1 ? '' : 's'}`
        : `${jobExpenses.length} captured expense${jobExpenses.length === 1 ? '' : 's'}`,
    },
    maintenance: {
      value: formatCurrency(maintenanceTotal),
      detail: `${maintenanceRecords.length} maintenance record${maintenanceRecords.length === 1 ? '' : 's'}`,
    },
    submissions: {
      value: `${billingSubmissions.length} submission${billingSubmissions.length === 1 ? '' : 's'}`,
      detail: 'Billing submissions and their finance approval status',
    },
  };

  const commercialSnapshot = useMemo(() => {
    if (!billingRules.is_synced) {
      return [
        {
          label: 'Labour coverage',
          value: '—',
          icon: Clock3,
          description: 'Press Sync Billing to calculate',
        },
        {
          label: 'Travel recovery',
          value: '—',
          icon: Navigation,
          description: 'Press Sync Billing to calculate',
        },
        {
          label: 'Related scope',
          value: relatedItems > 0 ? `${relatedItems} linked` : multipleRoots ? `${resolvedRootItemIds.length} roots` : 'Direct only',
          icon: FolderKanban,
          description: showMaintenance ? 'Includes linked appointments, assigned teams, and asset maintenance scope' : 'Current work item, linked appointments, and assigned team scope',
        },
      ];
    }
    return [
      {
        label: 'Labour coverage',
        value: formatCurrency(labourRecoveryEstimate),
        icon: Clock3,
        description: `${formatHours(onsiteHours)} onsite billed at client labour rates`,
      },
      {
        label: 'Travel recovery',
        value: formatCurrency(travelRecoveryEstimate),
        icon: Navigation,
        description: billingRules.min_travel_time_hours > 0 || billingRules.min_travel_distance_km > 0
          ? `${formatHours(travelHours)} + ${travelKm.toFixed(1)} km (with min thresholds applied)`
          : `${formatHours(travelHours)} + ${travelKm.toFixed(1)} km at current travel rates`,
      },
      {
        label: 'Related scope',
        value: relatedItems > 0 ? `${relatedItems} linked` : multipleRoots ? `${resolvedRootItemIds.length} roots` : 'Direct only',
        icon: FolderKanban,
        description: showMaintenance ? 'Includes linked appointments, assigned teams, and asset maintenance scope' : 'Current work item, linked appointments, and assigned team scope',
      },
    ];
  }, [billingRules.is_synced, billingRules.min_travel_time_hours, billingRules.min_travel_distance_km, labourRecoveryEstimate, onsiteHours, travelRecoveryEstimate, travelHours, travelKm, relatedItems, multipleRoots, resolvedRootItemIds.length, showMaintenance]);

  const sectionGridClass = showMaintenance ? 'xl:grid-cols-5' : 'md:grid-cols-4';
  const informationalAggregateNote = multipleRoots
    ? 'Costing and snapshot totals consolidate the linked appointments in this legacy group. Manual capture sections below still target the current anchor work item for edits and new entries.'
    : null;

  const jobSummaryCards = useMemo(() => {
    const cards = [
      ...(ledgerGenerated ? [{
        key: 'submissions',
        sectionId: 'submissions' as BillingSectionId,
        label: 'Submission',
        value: isClosedJobBillingReadOnly ? 'Read-only' : 'Ready to review',
        detail: isClosedJobBillingReadOnly
          ? 'Closed jobs keep billing submissions visible for review'
          : 'Open submissions to finalize and submit the finance handoff',
        icon: FileText,
        iconClassName: 'text-violet-500',
      }] : []),
      {
        key: 'time',
        sectionId: 'time' as BillingSectionId,
        label: 'Timers',
        value: formatHours(billableHours),
        detail: `${formatHours(onsiteHours)} onsite · ${travelKm.toFixed(1)} km travel`,
        icon: Clock3,
        iconClassName: 'text-sky-500',
      },
      {
        key: 'expenses',
        sectionId: 'submissions' as BillingSectionId,
        label: 'Expenses',
        value: formatCurrency(expensesTotal),
        detail: `${jobExpenses.length} captured expense${jobExpenses.length === 1 ? '' : 's'}`,
        icon: Receipt,
        iconClassName: 'text-emerald-500',
      },
      {
        key: 'costing',
        sectionId: 'costing' as BillingSectionId,
        label: 'Dashboard',
        value: billingRules.is_synced ? formatCurrency(recoverablesTotal) : '—',
        detail: billingRules.is_synced
          ? `${bomItems.length} lines · ${jobExpenses.length} expense${jobExpenses.length === 1 ? '' : 's'}${callOutFeeTotal > 0 ? ` · Call-out: ${formatCurrency(callOutFeeTotal)}` : ''}`
          : 'Press Sync Billing to calculate',
        icon: DollarSign,
        iconClassName: billingRules.is_synced ? 'text-amber-500' : 'text-gray-400',
      },
    ];

    if (jobBillingWorkspaceAccess === false) {
      return cards.filter((c) => c.sectionId === 'time');
    }
    return cards;
  }, [billableHours, bomItems.length, callOutFeeTotal, ledgerGenerated, expensesTotal, isClosedJobBillingReadOnly, jobBillingWorkspaceAccess, jobExpenses.length, onsiteHours, recoverablesTotal, travelKm, billingRules.is_synced]);

  const draftSubmission = billingSubmissions.find((s) => s.status === 'draft');
  const filterSettingsChanged = useMemo(() => {
    if (!draftSubmission) return false;
    const sub = draftSubmission;
    // If timer_aggregation_mode was never stored, treat any current value as changed
    if (sub.timer_aggregation_mode) {
      if (sub.timer_aggregation_mode !== timerAggregationMode) return true;
    } else if (timerAggregationMode) {
      return true;
    }
    // Compare billing_settings; if never stored, assume defaults (all true)
    const storedSettings = sub.billing_settings ?? {
      bill_travel_time: true,
      bill_travel_km: true,
      bill_onsite_labour: true,
    };
    if (storedSettings.bill_travel_time !== billingSettings.bill_travel_time) return true;
    if (storedSettings.bill_travel_km !== billingSettings.bill_travel_km) return true;
    if (storedSettings.bill_onsite_labour !== billingSettings.bill_onsite_labour) return true;
    return false;
  }, [draftSubmission, timerAggregationMode, billingSettings.bill_travel_time, billingSettings.bill_travel_km, billingSettings.bill_onsite_labour]);

  const jobDashboardModuleCount = [
    1,
    jobFinancialInsights ? 1 : 0,
    jobFinancialInsights?.documents?.length ? 1 : 0,
  ].reduce((sum, count) => sum + count, 0);

  const jobSectionCounts: Record<JobBillingPrimarySectionId, number> = {
    ledger: 0,
    costing: jobDashboardModuleCount,
    time: timeSummary?.total_entries || 0,
    expenses: jobExpenses.length,
    submissions: billingSubmissions.length,
  };

  if (!showDetailedWorkspace) {
    return (
      <JobCostingDashboard
        permissions={permissions}
        templateKey={templateKey}
        itemTypes={resolvedItemTypes}
        rootItemIds={resolvedRootItemIds.length > 0 ? resolvedRootItemIds : undefined}
        includeDescendants={resolvedIncludeDescendants}
        title={resolvedTitle}
        subtitle={resolvedSubtitle}
        embedded={embedded}
        showHeaderControls={showHeaderControls}
        onOpenItem={onOpenItem}
        onStatsChange={onStatsChange}
        showOutsourcing={jobCostingConfig.showOutsourcing}
        billingIntegrationMode={jobCostingConfig.billingIntegrationMode}
      />
    );
  }

  return (
    <div className="space-y-5" data-tour-id="work-item-billing-tab">
      {showHeaderControls && (
        <div>
          <div className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
            {resolvedTitle} <UserDocsLink docsPath="/total-operations/workflows/service-operations/billing-submissions" label="User guide: Billing submissions" />
          </div>
          <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{resolvedSubtitle}</p>
        </div>
      )}

      {canAccessJobBillableLedger && isClosedJobBillingReadOnly && isJobWorkspace && (
        <div className={`rounded-xl border p-4 ${isDark ? 'border-sky-800 bg-sky-950/20' : 'border-sky-200 bg-sky-50'}`}>
          <div className="flex items-start gap-3">
            <FileText className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isDark ? 'text-sky-300' : 'text-sky-600'}`} />
            <div>
              <div className={`text-sm font-semibold ${isDark ? 'text-sky-200' : 'text-sky-800'}`}>
                Billing ledger available in read-only mode
              </div>
              <p className={`mt-1 text-sm ${isDark ? 'text-sky-300/80' : 'text-sky-700'}`}>
                This job is closed, so the billable ledger stays visible for review without allowing edits or a new finance submission. Re-open the job if the billing pack needs changes.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Linked Invoice Display */}
      {jobFinancialInsights?.documents && jobFinancialInsights.documents.length > 0 && (
        <div className={`rounded-xl border p-4 ${isDark ? 'border-gray-700 bg-gray-900/50' : 'border-gray-200 bg-gray-50/50'}`}>
          <div className={`text-sm font-semibold mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            Linked Documents
          </div>
          <div className="space-y-2">
            {jobFinancialInsights.documents.map((doc) => (
              <div key={doc.id} className={`flex items-center justify-between p-3 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                <div className="flex items-center gap-3">
                  <FileText className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                  <div>
                    <div className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                      {doc.item_number} — {doc.item_type}
                    </div>
                    <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                      Generated: {formatDateTimeSAST(doc.created_at)}
                    </div>
                  </div>
                </div>
                <div className={`px-2 py-1 rounded text-xs font-medium ${getDocumentStatusPillClass(doc.status, isDark)}`}>
                  {doc.status}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sync Billing Controls - Only show when billing is not blocked */}
      {canAccessJobBillableLedger && isJobWorkspace && showHeaderControls && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              setBillingRules(prev => ({ ...prev, is_synced: !prev.is_synced }));
              notify.success(billingRules.is_synced ? 'Billing reset' : 'Billing synced');
            }}
            disabled={billingWorkspaceReadOnly}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${billingWorkspaceReadOnly ? 'cursor-not-allowed opacity-60 ' : ''
              }${billingRules.is_synced
                ? (isDark ? 'bg-emerald-900/40 text-emerald-300 border border-emerald-800' : 'bg-emerald-100 text-emerald-700 border border-emerald-200')
                : (isDark ? 'bg-blue-900/40 text-blue-300 border border-blue-800' : 'bg-blue-100 text-blue-700 border border-blue-200')
              }`}
          >
            <RefreshCw className={`w-4 h-4 ${billingRules.is_synced ? '' : 'animate-pulse'}`} />
            {billingRules.is_synced ? 'Recalculate' : 'Sync Billing'}
          </button>
          <button
            type="button"
            onClick={() => setShowBillingRulesPanel(!showBillingRulesPanel)}
            disabled={billingWorkspaceReadOnly}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${billingWorkspaceReadOnly ? 'cursor-not-allowed opacity-60 ' : ''
              }${isDark ? 'bg-gray-800 text-gray-300 border border-gray-700' : 'bg-gray-100 text-gray-700 border border-gray-200'
              }`}
          >
            <Settings className="w-4 h-4" />
            Rules
          </button>
        </div>
      )}

      {/* Billing Lock Banner — shown when charge influencers are frozen */}
      {billingLocked && (
        <div className={`flex items-center gap-2 rounded-lg border-l-4 px-3 py-2 transition-all duration-300 ease-in-out ${isDark ? 'border-amber-500 bg-amber-950/30 text-amber-200' : 'border-amber-400 bg-amber-50 text-amber-800'}`}>
          <Lock className="h-4 w-4 flex-shrink-0" />
          <span className="text-xs font-medium">
            {billingStatusWorkItem?.status === 'billing_submitted'
              ? 'Billing submitted — charge influencers are frozen (ledger locked).'
              : 'Billing in review — charge influencers are locked. Submit or reject billing to unlock.'}
          </span>
        </div>
      )}

      {/* Timer Aggregation Mode Toggle — always visible for jobs (essential billing influencer) */}
      {isJobWorkspace && (
      <div className={`flex items-center gap-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
        <span className="text-xs font-medium">Timer billing:</span>
        <div className={`inline-flex rounded-lg border ${isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-gray-50'} ${billingLocked ? 'opacity-50 pointer-events-none' : ''}`}>
          <button
            type="button"
            onClick={() => handleTimerAggregationModeChange('individual')}
            disabled={billingLocked}
            className={`px-3 py-1.5 text-xs font-medium rounded-l-lg transition-colors ${timerAggregationMode === 'individual'
              ? (isDark ? 'bg-blue-900/50 text-blue-200' : 'bg-blue-100 text-blue-700')
              : (isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700')
              }`}
          >
            Individual
          </button>
          <button
            type="button"
            onClick={() => handleTimerAggregationModeChange('team')}
            disabled={billingLocked}
            className={`px-3 py-1.5 text-xs font-medium rounded-r-lg transition-colors border-l ${isDark ? 'border-gray-700' : 'border-gray-200'} ${timerAggregationMode === 'team'
              ? (isDark ? 'bg-blue-900/50 text-blue-200' : 'bg-blue-100 text-blue-700')
              : (isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700')
              }`}
          >
            Team
          </button>
        </div>
        <span className={`text-[11px] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
          {timerAggregationMode === 'individual'
            ? 'Each member billed separately'
            : 'Team billed as one unit per day'}
        </span>
      </div>
      )}

      {/* Charge Type Toggles — persisted in localStorage across jobs */}
      <div className={`flex flex-wrap items-center gap-4 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
        <span className="text-xs font-medium">Charge types:</span>
        <label className={`flex items-center gap-1.5 cursor-pointer select-none ${billingWorkspaceReadOnly || billingLocked ? 'opacity-60' : ''}`}>
          <input
            type="checkbox"
            checked={billingSettings.bill_onsite_labour}
            onChange={(e) => handleChargeToggle('bill_onsite_labour', e.target.checked)}
            disabled={billingWorkspaceReadOnly || billingLocked}
            className={`h-4 w-4 rounded border ${isDark ? 'border-gray-600 bg-gray-800' : 'border-gray-300 bg-white'} accent-blue-500`}
          />
          <span className="text-xs">Onsite Time</span>
        </label>
        <label className={`flex items-center gap-1.5 cursor-pointer select-none ${billingWorkspaceReadOnly || billingLocked ? 'opacity-60' : ''}`}>
          <input
            type="checkbox"
            checked={billingSettings.bill_travel_time}
            onChange={(e) => handleChargeToggle('bill_travel_time', e.target.checked)}
            disabled={billingWorkspaceReadOnly || billingLocked}
            className={`h-4 w-4 rounded border ${isDark ? 'border-gray-600 bg-gray-800' : 'border-gray-300 bg-white'} accent-blue-500`}
          />
          <span className="text-xs">Travel Time</span>
        </label>
        <label className={`flex items-center gap-1.5 cursor-pointer select-none ${billingWorkspaceReadOnly || billingLocked ? 'opacity-60' : ''}`}>
          <input
            type="checkbox"
            checked={billingSettings.bill_travel_km}
            onChange={(e) => handleChargeToggle('bill_travel_km', e.target.checked)}
            disabled={billingWorkspaceReadOnly || billingLocked}
            className={`h-4 w-4 rounded border ${isDark ? 'border-gray-600 bg-gray-800' : 'border-gray-300 bg-white'} accent-blue-500`}
          />
          <span className="text-xs">Travel KMs</span>
        </label>
      </div>

      {/* Billing Rules Configuration Panel */}
      {showBillingRulesPanel && isJobWorkspace && (
        <div className={`rounded-xl border p-4 ${isDark ? 'border-gray-700 bg-gray-900/50' : 'border-gray-200 bg-gray-50/50'}`}>
          <div className={`text-sm font-semibold mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            Billing Rules Configuration
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Min Travel Time (hours)
              </label>
              <input
                type="number"
                min="0"
                step="0.5"
                value={billingRules.min_travel_time_hours}
                onChange={(e) => setBillingRules(prev => ({ ...prev, min_travel_time_hours: parseFloat(e.target.value) || 0, is_synced: false }))}
                className={`w-full px-3 py-2 rounded-lg text-sm border ${isDark ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-white border-gray-300 text-gray-700'}`}
              />
              <p className={`mt-1 text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Travel charged only if exceeds threshold</p>
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Min Travel Distance (km)
              </label>
              <input
                type="number"
                min="0"
                step="1"
                value={billingRules.min_travel_distance_km}
                onChange={(e) => setBillingRules(prev => ({ ...prev, min_travel_distance_km: parseFloat(e.target.value) || 0, is_synced: false }))}
                className={`w-full px-3 py-2 rounded-lg text-sm border ${isDark ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-white border-gray-300 text-gray-700'}`}
              />
              <p className={`mt-1 text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Distance charged only if exceeds threshold</p>
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Call-out Fee (R)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={billingRules.call_out_fee_per_appointment}
                onChange={(e) => setBillingRules(prev => ({ ...prev, call_out_fee_per_appointment: parseFloat(e.target.value) || 0, is_synced: false }))}
                className={`w-full px-3 py-2 rounded-lg text-sm border ${isDark ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-white border-gray-300 text-gray-700'}`}
              />
              <p className={`mt-1 text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Flat fee per appointment</p>
            </div>
          </div>
        </div>
      )}

      {isJobWorkspace ? (
        <>
          {!hideInternalNavigation && (
            <>
              <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                {jobSummaryCards.map((card) => {
                  const Icon = card.icon;
                  const isSelected = card.sectionId === activeSection;
                  return (
                    <button
                      key={card.key}
                      onClick={() => setActiveSection(card.sectionId)}
                      className={`rounded-xl border px-4 py-3 text-left transition-colors ${isSelected
                        ? isDark ? 'border-blue-700 bg-blue-900/30' : 'border-blue-300 bg-blue-50/70'
                        : isDark ? 'border-gray-800 bg-gray-900/70 hover:border-gray-700 hover:bg-gray-800/60' : 'border-gray-200 bg-gray-50/70 hover:border-gray-300 hover:bg-gray-100/60'
                        }`}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`rounded-lg p-1.5 ${isSelected ? (isDark ? 'bg-blue-800/60' : 'bg-blue-100 border border-blue-200') : (isDark ? 'bg-gray-800 text-gray-200' : 'bg-white text-gray-700 border border-gray-200')}`}>
                          <Icon className={`w-4 h-4 ${card.iconClassName}`} />
                        </div>
                        <span className={`text-[11px] font-semibold uppercase tracking-[0.14em] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{card.label}</span>
                      </div>
                      <p className={`mt-2 text-lg font-semibold ${isSelected ? (isDark ? 'text-blue-300' : 'text-blue-700') : (isDark ? 'text-gray-100' : 'text-gray-900')}`}>
                        {card.value}
                      </p>
                      <p className={`mt-1 text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                        {card.detail}
                      </p>
                    </button>
                  );
                })}
              </div>

              <div className={`flex gap-1 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`} data-tour-id="billing-section-nav">
                {jobPrimarySections.map((section) => {
                  const Icon = section.icon;
                  const isSelected = section.id === activeSection;
                  const count = jobSectionCounts[section.id];
                  return (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors ${isSelected
                        ? 'text-blue-500'
                        : `${isDark ? 'border-transparent text-gray-400 hover:text-gray-300' : 'border-transparent text-gray-500 hover:text-gray-700'}`
                        }`}
                      style={isSelected ? { borderColor: accentColor, color: accentColor } : undefined}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      {section.label}
                      {count > 0 && (
                        <span className={`px-1.5 py-0.5 text-[10px] rounded-full ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                          {count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </>
      ) : (
        <div className={`overflow-hidden rounded-2xl border shadow-sm ${isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'}`}
          style={hideInternalNavigation ? { display: 'none' } : undefined}
        >
          <div className={`grid divide-y lg:grid-cols-4 lg:divide-x lg:divide-y-0 ${isDark ? 'divide-gray-800 bg-gray-900' : 'divide-gray-200 bg-white'}`}>
            {[
              {
                label: 'Billable hours',
                value: formatHours(billableHours),
                meta: `${formatHours(totalHours)} logged`,
                icon: Clock3,
              },
              {
                label: 'Travel recorded',
                value: formatHours(travelHours),
                meta: travelKm > 0 ? `${travelKm.toFixed(1)} km tracked` : 'No travel distance logged',
                icon: Navigation,
              },
              {
                label: 'Billing lines',
                value: formatCurrency(billableLinesTotal),
                meta: `${bomItems.length} line${bomItems.length === 1 ? '' : 's'} ready`,
                icon: Package,
              },
              {
                label: showMaintenance ? 'Recovery & upkeep' : 'Recoverable spend',
                value: formatCurrency(showMaintenance ? expensesTotal + maintenanceTotal : expensesTotal),
                meta: showMaintenance
                  ? `${maintenanceRecords.length} maintenance record${maintenanceRecords.length === 1 ? '' : 's'}`
                  : `${relatedItems} related item${relatedItems === 1 ? '' : 's'} in scope`,
                icon: showMaintenance ? Wrench : Receipt,
              },
            ].map((card) => {
              const Icon = card.icon;
              return (
                <div key={card.label} className="min-h-[92px] px-4 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <span className={`text-[11px] font-semibold uppercase tracking-[0.16em] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                      {card.label}
                    </span>
                    <Icon className={`h-4 w-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                  </div>
                  <div className={`mt-2 text-[28px] font-semibold tracking-tight leading-none ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {card.value}
                  </div>
                  <div className={`mt-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {card.meta}
                  </div>
                </div>
              );
            })}
          </div>

          {!isJobWorkspace && (
            <div className={`grid border-t lg:grid-cols-3 ${isDark ? 'border-gray-800 bg-gray-950/60' : 'border-gray-200 bg-gray-50/60'}`}>
              {commercialSnapshot.map((item, index) => {
                const Icon = item.icon;
                return (
                  <div
                    key={item.label}
                    className={`px-4 py-3 ${index > 0 ? isDark ? 'border-t border-gray-800 lg:border-l lg:border-t-0' : 'border-t border-gray-200 lg:border-l lg:border-t-0' : ''}`}
                  >
                    <div className="flex items-center gap-2">
                      <div className={`rounded-lg p-2 ${isDark ? 'bg-gray-800 text-gray-200' : 'bg-white text-gray-700 border border-gray-200'}`}>
                        <Icon className="h-4 w-4" style={{ color: accentColor }} />
                      </div>
                      <div>
                        <div className={`text-[11px] font-semibold uppercase tracking-[0.16em] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                          {item.label}
                        </div>
                        <div className={`mt-0.5 text-base font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{item.value}</div>
                      </div>
                    </div>
                    <div className={`mt-1.5 text-[11px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{item.description}</div>
                  </div>
                );
              })}
            </div>
          )}

          {!isJobWorkspace && (
            <div className={`grid border-t md:grid-cols-2 xl:grid-cols-4 ${isDark ? 'border-gray-800 bg-gray-950/40' : 'border-gray-200 bg-gray-50/40'}`}>
              {quickActionCards.map((item, index) => {
                const Icon = item.icon;
                return (
                  <div
                    key={item.label}
                    className={`px-4 py-3 ${index > 0 ? isDark ? 'border-t border-gray-800 md:border-l md:border-t-0' : 'border-t border-gray-200 md:border-l md:border-t-0' : ''}`}
                  >
                    <div className="flex items-center gap-2">
                      <div className={`rounded-lg p-2 ${isDark ? 'bg-gray-800 text-gray-200' : 'bg-white text-gray-700 border border-gray-200'}`}>
                        <Icon className="h-4 w-4" style={{ color: accentColor }} />
                      </div>
                      <div>
                        <div className={`text-[11px] font-semibold uppercase tracking-[0.16em] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                          {item.label}
                        </div>
                        <div className={`mt-0.5 text-base font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{item.value}</div>
                      </div>
                    </div>
                    <div className={`mt-1.5 text-[11px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{item.description}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {informationalAggregateNote && (
        <div className={`rounded-xl border px-4 py-3 text-[13px] ${isDark ? 'border-amber-800/60 bg-amber-950/20 text-amber-200' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
          {informationalAggregateNote}
        </div>
      )}

      {!isJobWorkspace && !hideInternalNavigation && (
        <div className={`overflow-hidden rounded-2xl border ${isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'}`}>
          <div className={`border-b px-4 py-3 ${isDark ? 'border-gray-800 bg-gray-900/90' : 'border-gray-200 bg-gray-50/80'}`}>
            <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <div className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                  Workspace navigator
                </div>
                <p className={`mt-0.5 text-[13px] ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  Jump between the billable ledger, costing, timers, and expense workflows without leaving the billing workspace.
                </p>
              </div>
              <div className={`text-[11px] font-medium ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                {sections.length} sections available
              </div>
            </div>
          </div>

          <div className={`grid ${sectionGridClass}`}>
            {sections.map((section) => {
              const Icon = section.icon;
              const isSelected = section.id === activeSection;
              const metric = sectionMetrics[section.id];
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`relative flex min-h-[104px] w-full items-start gap-3 px-3.5 py-3 text-left transition ${isSelected
                    ? isDark
                      ? 'bg-gray-800 text-white'
                      : 'bg-blue-50/70 text-gray-900'
                    : isDark
                      ? 'text-gray-300 hover:bg-gray-800/80'
                      : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  style={{ boxShadow: 'none' }}
                >
                  <span
                    className="absolute inset-x-0 top-0 h-[3px]"
                    style={{ backgroundColor: isSelected ? accentColor : 'transparent' }}
                  />
                  <div className={`mt-0.5 rounded-lg p-2 ${isSelected ? (isDark ? 'bg-gray-900/80' : 'bg-white/90 border border-blue-100') : (isDark ? 'bg-gray-900/60' : 'bg-gray-50 border border-gray-100')}`}>
                    <Icon className={`h-4 w-4 flex-shrink-0 ${isSelected ? '' : isDark ? 'text-gray-500' : 'text-gray-400'}`} style={isSelected ? { color: accentColor } : undefined} />
                  </div>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[13px] font-semibold">{section.label}</span>
                    <span className={`mt-0.5 block text-lg font-semibold tracking-tight ${isSelected ? isDark ? 'text-white' : 'text-gray-900' : isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                      {metric?.value}
                    </span>
                    <span className={`mt-0.5 block text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                      {metric?.detail}
                    </span>
                    {isSelected && (
                      <span className={`mt-2 inline-flex items-center gap-1 text-[11px] font-medium ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
                        Active section
                        <ArrowRight className="h-3.5 w-3.5" />
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className={(isJobWorkspace || hideInternalNavigation) ? '' : `overflow-hidden rounded-2xl border shadow-sm ${isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'}`}>
        {!isJobWorkspace && !hideInternalNavigation && (
          <div className={`flex flex-col gap-2 border-b px-4 py-3 lg:flex-row lg:items-center lg:justify-between ${isDark ? 'border-gray-800 bg-gray-900/90' : 'border-gray-200 bg-gray-50/80'}`}>
            <div>
              <div className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                {currentSection?.label}
              </div>
              <p className={`mt-0.5 text-[13px] ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                {currentSection?.description}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {sectionMetrics[currentSection.id] && (
                <div className={`inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-[11px] font-medium ${isDark ? 'bg-emerald-900/20 text-emerald-300 border border-emerald-800/50' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'}`}>
                  <DollarSign className="h-3.5 w-3.5" />
                  {sectionMetrics[currentSection.id].value}
                </div>
              )}
              {resolvedWorkItem?.item_number && (
                <div className={`inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-[11px] font-medium ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-600 border border-gray-200'}`}>
                  <Briefcase className="h-3.5 w-3.5" />
                  {resolvedWorkItem.item_number}
                  <ArrowRight className="h-3.5 w-3.5 opacity-60" />
                  <span>{currentSection?.label}</span>
                </div>
              )}
            </div>
          </div>
        )}

        <div className={(isJobWorkspace || hideInternalNavigation) ? '' : 'p-4'}>
          {activeSection === 'costing' && (
            <div className="space-y-4" data-tour-id="billing-costing-dashboard">
              {isJobWorkspace ? (
                <>
                  <DashboardPanel
                    title="Finance workspace"
                    description={isClosedJobBillingReadOnly
                      ? 'This closed job keeps billing submissions available for read-only review, alongside the linked finance documents already created from them.'
                      : 'Track timers, expenses, and linked finance documents from this job workspace. Use the Submissions tab to generate and manage billing submissions.'}
                    isDark={isDark}
                    actions={(
                      <>
                        <button
                          type="button"
                          onClick={() => setActiveSection('submissions')}
                          className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold transition ${isDark ? 'border border-violet-800/50 bg-violet-950/30 text-violet-300 hover:bg-violet-950/50' : 'border border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100'}`}
                        >
                          <FileText className="h-3.5 w-3.5" />
                          Open submissions
                        </button>
                        {isClosedJobBillingReadOnly && (
                          <span className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold ${isDark ? 'border border-sky-800/50 bg-sky-950/30 text-sky-300' : 'border border-sky-200 bg-sky-50 text-sky-700'}`}>
                            <AlertCircle className="h-3.5 w-3.5" />
                            Closed job · review only
                          </span>
                        )}
                      </>
                    )}
                  >
                    <div className="grid gap-3 p-4 md:grid-cols-3">
                      {quickActionCards.slice(0, 3).map((card) => (
                        <DashboardStatCard
                          key={card.label}
                          label={card.label}
                          value={card.value}
                          detail={card.description}
                          icon={card.icon}
                          isDark={isDark}
                          accentColor={accentColor}
                        />
                      ))}
                    </div>

                    <div className={`border-t px-4 py-3 text-[13px] ${isDark ? 'border-gray-800 bg-gray-950/30 text-gray-400' : 'border-gray-200 bg-gray-50/50 text-gray-600'}`}>
                      {ledgerGenerated
                        ? (isClosedJobBillingReadOnly
                          ? 'This job is closed, so billing submissions stay viewable here without allowing edits. Re-open the job if another submission is required.'
                          : financeWorkspaceAccess.isFinanceLike
                            ? 'Keep this page focused on prep: sync the timers, tidy the billing lines, then submit the billing pack when the handoff is ready for finance.'
                            : 'This workspace stays in prep mode until a finance user reviews the billing submission, keeping the job clean and avoiding accidental document release.')
                        : 'Use this workspace to keep time, expenses, and linked finance activity in view while the job progresses. Generate billing when ready to hand off to finance.'}
                    </div>

                  </DashboardPanel>

                  {jobFinancialInsights ? (
                    <>
                      <DashboardPanel
                        title="Executive snapshot"
                        description="Only the core financial picture for this job: value prepared, spend captured, and whether the budget is drifting."
                        isDark={isDark}
                        actions={budgetHealth ? (
                          <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-[12px] font-medium ${budgetHealth.pillClass}`}>
                            <span className={`inline-block h-2.5 w-2.5 rounded-full ${budgetHealth.dot}`} />
                            {budgetHealth.label}
                          </div>
                        ) : null}
                      >
                        <div className="grid gap-3 p-4 md:grid-cols-2 xl:grid-cols-3">
                          {financialHealthCards.map((card) => (
                            <DashboardStatCard
                              key={card.label}
                              label={card.label}
                              value={card.value}
                              detail={card.detail}
                              icon={card.icon}
                              isDark={isDark}
                              accentColor={accentColor}
                              valueClassName={card.valueClassName}
                            />
                          ))}
                        </div>
                      </DashboardPanel>

                      {jobFinancialInsights.profitability.is_over_budget && jobFinancialInsights.profitability.total_budget > 0 && (
                        <div className={`flex items-start gap-3 rounded-xl border px-4 py-3 ${isDark ? 'border-red-800/60 bg-red-950/20 text-red-200' : 'border-red-200 bg-red-50 text-red-800'}`}>
                          <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0" />
                          <div>
                            <div className="text-sm font-semibold">Budget overrun warning</div>
                            <div className={`mt-1 text-[13px] ${isDark ? 'text-red-300' : 'text-red-700'}`}>
                              Captured spend is {jobFinancialInsights.profitability.budget_variance_pct.toFixed(1)}% over the tracked job budget.
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <DashboardPanel
                      title="Executive snapshot"
                      description="Commercial summary data will appear here as soon as the job financial summary is available."
                      isDark={isDark}
                    >
                      <div className={`px-4 py-5 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        No financial summary has been generated for this job yet.
                      </div>
                    </DashboardPanel>
                  )}
                </>
              ) : (
                <DashboardPanel
                  title="Costing dashboard"
                  description="Combined costing, margin, labour, travel, and profitability view for the current billing scope."
                  isDark={isDark}
                >
                  <JobCostingDashboard
                    permissions={permissions}
                    templateKey={templateKey}
                    itemTypes={resolvedItemTypes}
                    rootItemIds={resolvedRootItemIds.length > 0 ? resolvedRootItemIds : undefined}
                    includeDescendants={resolvedIncludeDescendants}
                    title={resolvedTitle}
                    subtitle={resolvedSubtitle}
                    embedded
                    showHeaderControls={false}
                    onOpenItem={onOpenItem}
                    showOutsourcing={jobCostingConfig.showOutsourcing}
                    billingIntegrationMode={jobCostingConfig.billingIntegrationMode}
                  />
                </DashboardPanel>
              )}
            </div>
          )}

          {activeSection === 'time' && (
            <div className="space-y-4">
              {isProject && projectTabWorkItem ? (
                <ProjectTimeTrackingTab
                  workItem={projectTabWorkItem}
                />
              ) : workItemId ? (
                <TimeTrackingTab
                  workItemId={workItemId}
                  relatedWorkItemIds={relatedTimeTrackingIds.length > 0 ? relatedTimeTrackingIds : undefined}
                  isActive={isActive}
                  isDark={isDark}
                  readOnly={billingWorkspaceReadOnly || !canEditBillingTimers || billingLocked}
                  billingLocked={billingLocked}
                  openComposerToken={timeComposerToken}
                  liveTimer={getAppointmentLiveTimer(workItemId)}
                  timerAggregationMode={timerAggregationMode}
                  billingSettings={billingSettings}
                  workItemType={resolvedWorkItemType}
                />
              ) : null}
              {workItemId && (
                <BillingCommentsSection
                  workItemId={workItemId}
                  isActive={isActive && activeSection === 'time'}
                  embedded
                  includeRelated={Boolean(includeRelated ?? (resolvedWorkItemType === 'job'))}
                  readOnly={billingWorkspaceReadOnly || (isJobWorkspace && jobBillingWorkspaceAccess === false)}
                />
              )}

              {/* BOM / Billing items from child appointments */}
              {bomItems.length > 0 && (
                <div className={`rounded-xl border ${isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'}`}>
                  <div className={`flex items-center gap-2 border-b px-4 py-2.5 ${isDark ? 'border-gray-800 bg-gray-900/90' : 'border-gray-200 bg-gray-50/80'}`}>
                    <Package className="h-3.5 w-3.5 text-blue-500" />
                    <span className={`text-[11px] font-semibold uppercase tracking-[0.16em] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      Billing items ({bomItems.length})
                    </span>
                  </div>
                  <div className="space-y-2 p-3">
                    {bomItems.map((item) => {
                      const unitCost = Number(item.unit_cost) || 0;
                      const lineTotal = Number(item.line_total) || unitCost * item.quantity;
                      return (
                        <div
                          key={item.id}
                          className={`flex items-center gap-3 p-2.5 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}
                        >
                          <div className={`p-1.5 rounded-lg ${isDark ? 'bg-blue-900/30' : 'bg-blue-100'}`}>
                            <Package className="h-4 w-4 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              {item.item_name || item.description || 'Unnamed item'}
                            </p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                {item.quantity} {item.unit || ''}
                              </span>
                              {unitCost > 0 && (
                                <span className="text-[10px] text-gray-400">
                                  @ R{unitCost.toFixed(2)}
                                </span>
                              )}
                            </div>
                          </div>
                          {lineTotal > 0 && (
                            <span className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              R{lineTotal.toFixed(2)}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Expenses — all statuses shown, only approved are billable */}
              {isJobWorkspace && workItemId && (
                <div className={`rounded-xl border ${isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'}`}>
                  <div className={`flex items-center gap-2 border-b px-4 py-2.5 ${isDark ? 'border-gray-800 bg-gray-900/90' : 'border-gray-200 bg-gray-50/80'}`}>
                    <Receipt className="h-3.5 w-3.5 text-amber-500" />
                    <span className={`text-[11px] font-semibold uppercase tracking-[0.16em] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      Expenses ({jobExpenses.length})
                    </span>
                  </div>
                  <div className="p-3">
                    <JobExpensesTab
                      workItemId={workItemId}
                      workItem={{
                        id: workItemId,
                        item_number: resolvedWorkItem?.item_number,
                        title: resolvedWorkItem?.title,
                      }}
                      isActive={isActive && activeSection === 'time'}
                      readOnly={billingWorkspaceReadOnly}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {activeSection === 'submissions' && workItemId && (
            <div className="space-y-3" data-tour-id="billing-submissions-section">
              <DashboardPanel
                title="Billing submissions"
                description="All billing submissions generated from this job, their finance approval status, and actions to submit or open them."
                isDark={isDark}
                actions={
                  <div className="flex items-center gap-2">
                    {!isClosedJobBillingReadOnly && (
                      <select
                        value={requestedDocType}
                        onChange={(e) => setRequestedDocType(e.target.value as 'quote' | 'proforma' | 'invoice')}
                        className={`rounded-lg border px-2 py-1.5 text-xs font-semibold transition ${isDark ? 'border-gray-700 bg-gray-800 text-gray-200' : 'border-gray-200 bg-white text-gray-700'}`}
                        title="Document type for billing submission"
                        data-tour-id="billing-doc-type-select"
                      >
                        <option value="invoice">Invoice</option>
                        <option value="quote">Quote</option>
                        <option value="proforma">Pro-forma</option>
                      </select>
                    )}
                    <button
                      type="button"
                      onClick={() => void loadBillingSubmissions()}
                      disabled={billingSubmissionsLoading}
                      className={`inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-semibold transition ${billingSubmissionsLoading ? 'opacity-60 cursor-not-allowed' : ''} ${isDark ? 'border border-gray-700 bg-gray-800 text-gray-200 hover:bg-gray-700' : 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-50'}`}
                    >
                      {billingSubmissionsLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                      Refresh
                    </button>
                  </div>
                }
              >
                {billingSubmissionsLoading ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
                  </div>
                ) : billingSubmissions.length > 0 ? (
                  <div className={`divide-y ${isDark ? 'divide-gray-800' : 'divide-gray-200'}`}>
                    {billingSubmissions.map((submission) => {
                      const lineItemCount = submission.line_items?.length || 0;
                      const submissionTotal = (submission.line_items || []).reduce((sum, li) => sum + (Number(li.line_total) || Number(li.unit_cost) * li.quantity || 0), 0);
                      const isPending = submission.status === 'pending_approval';
                      const statusLabel = getBillingSubmissionStatusLabel(submission.status);
                      const statusDescription = BILLING_SUBMISSION_STATUS_DESCRIPTIONS[submission.status] || '';
                      return (
                        <div
                          key={submission.id}
                          className={`flex flex-col gap-3 px-4 py-3 lg:flex-row lg:items-center lg:justify-between ${isDark ? 'bg-gray-900/70' : 'bg-white'}`}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <button
                                type="button"
                                onClick={() => onOpenDocumentModal?.(submission.id, 'billing_submission')}
                                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${isDark ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-700'}`}
                              >
                                <FileText className="h-3 w-3" />
                                {submission.item_number}
                              </button>
                              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${getDocumentStatusPillClass(submission.status, isDark)}`}>
                                {statusLabel}
                              </span>
                              <span className={`text-[11px] uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                {submission.requested_doc_type}
                              </span>
                            </div>
                            <div className={`mt-1 text-[12px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              {submission.currency} {submissionTotal.toFixed(2)} · {lineItemCount} line item{lineItemCount === 1 ? '' : 's'} · {formatDateSAST(submission.created_at)}
                            </div>
                            {statusDescription && (
                              <div className={`mt-0.5 text-[11px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                {statusDescription}
                              </div>
                            )}
                            {submission.notes && (
                              <div className={`mt-1 text-[12px] italic ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                &ldquo;{submission.notes}&rdquo;
                              </div>
                            )}
                          </div>

                          <div className="flex flex-wrap items-center gap-2">
                            {isPending && (
                              <span className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium ${isDark ? 'bg-yellow-900/20 text-yellow-300' : 'bg-yellow-50 text-yellow-700'}`}>
                                <Clock3 className="h-3.5 w-3.5" />
                                Awaiting finance review
                              </span>
                            )}
                            {submission.status === 'draft' && (lineItemCount === 0 || filterSettingsChanged) && !isClosedJobBillingReadOnly && canSubmitForBilling && (
                              <button
                                type="button"
                                onClick={handleGenerateBilling}
                                disabled={generatingBilling}
                                className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold transition ${generatingBilling ? 'opacity-60 cursor-not-allowed' : ''} ${isDark ? 'border border-amber-800/50 bg-amber-950/30 text-amber-300 hover:bg-amber-950/50' : 'border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100'}`}
                              >
                                {generatingBilling ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                                {generatingBilling ? 'Regenerating...' : (filterSettingsChanged && lineItemCount > 0 ? 'Regenerate (filters changed)' : 'Regenerate')}
                              </button>
                            )}
                            {submission.status !== 'draft' && !isClosedJobBillingReadOnly && canSubmitForBilling && (
                              <button
                                type="button"
                                onClick={handleGenerateBilling}
                                disabled={generatingBilling}
                                className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold transition ${generatingBilling ? 'opacity-60 cursor-not-allowed' : ''} ${isDark ? 'border border-violet-800/50 bg-violet-950/30 text-violet-300 hover:bg-violet-950/50' : 'border border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100'}`}
                              >
                                {generatingBilling ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                                {generatingBilling ? 'Generating...' : 'Generate New'}
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => onOpenDocumentModal?.(submission.id, 'billing_submission')}
                              className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold transition ${isDark ? 'border border-gray-700 bg-gray-800 text-gray-100 hover:bg-gray-700' : 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-50'}`}
                            >
                              <FileText className="h-3.5 w-3.5" />
                              Open
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className={`flex flex-col items-center justify-center gap-3 px-4 py-8 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    <FileText className={`h-8 w-8 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
                    <div className="text-sm">No billing submissions have been generated for this job yet.</div>
                    {!isClosedJobBillingReadOnly && canSubmitForBilling && (
                      <div className="flex items-center gap-2" data-tour-id="billing-generate-button">
                        <select
                          value={requestedDocType}
                          onChange={(e) => setRequestedDocType(e.target.value as 'quote' | 'proforma' | 'invoice')}
                          className={`rounded-lg border px-2 py-2 text-xs font-semibold transition ${isDark ? 'border-gray-700 bg-gray-800 text-gray-200' : 'border-gray-200 bg-white text-gray-700'}`}
                          title="Document type for billing submission"
                        >
                          <option value="invoice">Invoice</option>
                          <option value="quote">Quote</option>
                          <option value="proforma">Pro-forma</option>
                        </select>
                        <button
                          type="button"
                          onClick={handleGenerateBilling}
                          disabled={generatingBilling}
                          className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-xs font-semibold transition ${generatingBilling ? 'opacity-60 cursor-not-allowed' : ''} ${isDark ? 'border border-violet-800/50 bg-violet-950/30 text-violet-300 hover:bg-violet-950/50' : 'border border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100'}`}
                        >
                          {generatingBilling ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                          {generatingBilling ? 'Generating...' : `Generate ${requestedDocType === 'invoice' ? 'Invoice' : requestedDocType === 'quote' ? 'Quote' : 'Pro-forma'} Submission`}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </DashboardPanel>
            </div>
          )}


          {activeSection === 'maintenance' && showMaintenance && workItemId && (
            <AssetMaintenanceTab workItemId={workItemId} isActive={isActive && activeSection === 'maintenance'} />
          )}
        </div>
      </div>

      {overviewLoading && !hasLoadedRef.current && (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
        </div>
      )}
    </div>
  );
}